import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CURRENCIES } from '../constants/currencies';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { logout } from '../Store/slices/authSlice';
import BrandLogo from '../components/BrandLogo';
import StepTabs from '../components/StepTabs';
import StatusIcon from '../components/StatusIcon';
import PartnerAnalyticsPanel from './PartnerAnalyticsPanel';
import showNotification from '../Utility Components/iFitNotification';
import {
  addMemberStepOne,
  assignMemberToPlan,
  addPlan,
  bootstrapPartnerWorkspace,
  cancelMemberOnboarding,
  confirmMemberUpiPayment,
  deleteMember,
  deletePlan,
  editMember,
  editPlan,
  fetchAlertQueue,
  fetchBillingDashboard,
  fetchMemberPaymentQuote,
  fetchAvailablePlansForMember,
  fetchPlansByOutlet,
  fetchPlansForOutlets,
  fetchPartnerSubscriptions,
  initiateMemberUpiPayment,
  setSelectedOutletFilter,
  setSelectedOutletId,
  resumeMembershipHold,
  requestPlanUpgrade,
  startMembershipHold,
  triggerManualAlert,
  triggerOverdueAction,
  toggleMember,
  togglePlan,
  updateOutletSignupFee,
  uploadMemberDocuments,
  verifyMemberOtp
} from '../Store/slices/partnerWorkspaceSlice';

const MENU_ITEMS = [
  { key: 'Home', label: 'Home', icon: HomeOutlinedIcon },
  { key: 'My Outlets', label: 'My Outlets', icon: StorefrontOutlinedIcon },
  { key: 'My Members', label: 'My Members', icon: GroupsOutlinedIcon },
  { key: 'My Plans', label: 'My Plans', icon: AssignmentOutlinedIcon },
  { key: 'Plan Renewal', label: 'Plan Renewal', icon: AutorenewOutlinedIcon },
  { key: 'Billing', label: 'Billing', icon: AccountBalanceWalletOutlinedIcon },
  { key: 'Alert Queue', label: 'Alert Queue', icon: NotificationsActiveOutlinedIcon },
  { key: 'Analytics', label: 'Analytics', icon: QueryStatsOutlinedIcon }
];

const PLAN_STEP_LABELS = {
  1: 'Add Plan Details'
};

const MEMBER_ONBOARDING_STEP_LABELS = {
  1: 'Member Details',
  2: 'Verify Contact',
  3: 'Upload Documents',
  4: 'Select Plan',
  5: 'Payment Summary'
};

const EDIT_MEMBER_STEP_LABELS = {
  1: 'Edit Details',
  2: 'Upload Documents',
  3: 'Update Plan'
};

const WEIGHT_UNITS = [
  { value: 'kg', label: 'kg' },
  { value: 'lb', label: 'lb' }
];

const HEIGHT_UNITS = [
  { value: 'cm', label: 'cm' },
  { value: 'ft_in', label: 'ft/in' }
];

const toDateInputValue = (value) => {
  if (!value) {
    return '';
  }
  const text = String(value);
  const dateMatch = text.match(/^\d{4}-\d{2}-\d{2}/);
  if (dateMatch) {
    return dateMatch[0];
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const todayDateInputValue = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const calculatePlanEndDate = (startDateInput, durationMonths) => {
  if (!startDateInput) {
    return '';
  }
  const [year, month, day] = startDateInput.split('-').map(Number);
  if (!year || !month || !day) {
    return '';
  }
  const months = Math.max(Number(durationMonths) || 1, 1);
  const totalMonths = month - 1 + months;
  const endYear = year + Math.floor(totalMonths / 12);
  const endMonth = (totalMonths % 12) + 1;
  const daysInEndMonth = new Date(endYear, endMonth, 0).getDate();
  const endDay = Math.min(day, daysInEndMonth);
  return `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
};

const normalizeAvailablePlans = (records = []) =>
  (records || [])
    .map((record) => ({
      ...record,
      plan_id: record?.plan_id || record?.plan?.id || ''
    }))
    .filter((record) => record.plan_id);

const getPlanOptionLabel = (record) => {
  const plan = record?.plan || {};
  const name = plan.name || plan.description || record?.name || record?.description || 'Plan';
  const currency = plan.currency || record?.currency || 'INR';
  const charge = plan.charge ?? record?.charge ?? 0;
  return `${name} - ${currency} ${charge}`;
};

const getOutletPlanLabel = (record) => record?.plan?.name || record?.plan?.description || record?.name || record?.description || 'this plan';

function ContextTitle({ label, relation = 'for', name, standalone = false }) {
  return (
    <h2 className={`context-title${standalone ? ' context-title--standalone' : ''}`}>
      <span className="context-title__label">{label}</span>
      {name ? (
        <>
          <span className="context-title__relation">{relation}</span>
          <span className="context-title__name">{name}</span>
        </>
      ) : null}
    </h2>
  );
}

function VerificationPill({ verified, label }) {
  return (
    <span className={`status-pill ${verified ? 'active' : 'inactive'} verification-pill`}>
      {label} {verified ? 'Verified' : 'Pending'}
    </span>
  );
}

const isMemberIdentityVerified = (member) => Boolean(
  member?.is_document_uploaded ||
  member?.document_path ||
  member?.identity_document_path ||
  member?.kyc_document_path
);

const splitHeightInches = (height, unit) => {
  if (unit !== 'ft_in') {
    return { feet: '', inches: '' };
  }
  const totalInches = Number(height);
  if (!Number.isFinite(totalInches) || totalInches <= 0) {
    return { feet: '', inches: '' };
  }
  return {
    feet: String(Math.floor(totalInches / 12)),
    inches: String(Math.round((totalInches % 12) * 100) / 100)
  };
};

const combineHeightInput = ({ height, height_unit, height_feet, height_inches }) => {
  if (height_unit === 'ft_in') {
    if ((height_feet === '' || height_feet == null) && (height_inches === '' || height_inches == null)) {
      return null;
    }
    const feet = Number(height_feet || 0);
    const inches = Number(height_inches || 0);
    return (feet * 12) + inches;
  }
  if (height === '' || height == null) {
    return null;
  }
  return Number(height);
};

const roundToTwoDecimals = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return null;
  }
  return Math.round((numberValue + Number.EPSILON) * 100) / 100;
};

const formatCalculatedInput = (value) => {
  const roundedValue = roundToTwoDecimals(value);
  if (roundedValue == null) {
    return '';
  }
  return String(roundedValue);
};

const formatMoney = (currency = 'INR', value = 0) => {
  const amount = Number(value);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  return `${currency || 'INR'} ${safeAmount.toLocaleString(undefined, {
    minimumFractionDigits: safeAmount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  })}`;
};

const formatShortDate = (value) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const getDayDifferenceFromToday = (value) => {
  if (!value) {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) {
    return null;
  }
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const getRenewalRowId = (row) =>
  `${row?.user_id || 'member'}-${row?.outlet_id || 'outlet'}-${row?.plan_id || 'plan'}-${row?.due_date || 'due'}`;

const calculateRenewalPlanBreakup = (planRecord, row) => {
  const plan = planRecord?.plan || planRecord || {};
  const baseAmount = Number(plan.charge ?? planRecord?.charge ?? row?.base_amount ?? row?.amount ?? 0) || 0;
  const planDiscountPct = Math.min(Math.max(Number(plan.discount_pct ?? planRecord?.discount_pct ?? 0) || 0, 0), 100);
  const fixedDiscount = Math.max(Number(plan.discount_amount ?? planRecord?.discount_amount ?? 0) || 0, 0);
  const percentDiscount = Math.round(((baseAmount * planDiscountPct) / 100) * 100) / 100;
  const planDiscountAmount = Math.min(fixedDiscount > 0 ? fixedDiscount : percentDiscount, baseAmount);
  const amountAfterPlanDiscount = Math.max(baseAmount - planDiscountAmount, 0);
  const memberDiscountPct = row?.member_discount_mode === 'every_cycle'
    ? Math.min(Math.max(Number(row?.member_discount_pct || 0), 0), 100)
    : 0;
  const memberDiscountAmount = Math.round(((amountAfterPlanDiscount * memberDiscountPct) / 100) * 100) / 100;
  const discountAmount = Math.round((planDiscountAmount + memberDiscountAmount) * 100) / 100;
  const finalAmount = Math.max(Math.round((baseAmount - discountAmount) * 100) / 100, 0);

  return {
    plan_id: String(planRecord?.plan_id || plan?.id || row?.plan_id || ''),
    plan_name: plan.name || plan.description || planRecord?.name || row?.plan_name || 'Plan',
    currency: plan.currency || planRecord?.currency || row?.currency || 'INR',
    base_amount: baseAmount,
    plan_discount_pct: planDiscountPct,
    plan_discount_amount: planDiscountAmount,
    member_discount_mode: row?.member_discount_mode || 'none',
    member_discount_pct: memberDiscountPct,
    member_discount_amount: memberDiscountAmount,
    discount_pct: baseAmount > 0 ? Math.round(((discountAmount / baseAmount) * 100) * 100) / 100 : 0,
    discount_amount: discountAmount,
    signup_fee: 0,
    final_amount: finalAmount
  };
};

const toBreakupNumber = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const PriceBreakupModal = ({ row, onClose }) => {
  if (!row) {
    return null;
  }

  const currency = row.currency || 'INR';
  const baseAmount = toBreakupNumber(row.base_amount);
  const planDiscountAmount = toBreakupNumber(row.plan_discount_amount);
  const memberDiscountAmount = toBreakupNumber(row.member_discount_amount);
  const partnerDiscountAmount = toBreakupNumber(row.partner_discount_amount);
  const signupFee = toBreakupNumber(row.signup_fee);
  const dueAmount = toBreakupNumber(row.due_amount ?? row.amount ?? row.final_amount);
  const calculatedDiscountAmount = planDiscountAmount + memberDiscountAmount + partnerDiscountAmount;
  const totalDiscountAmount = toBreakupNumber(row.discount_amount) || calculatedDiscountAmount;
  const totalDiscountPct = toBreakupNumber(row.discount_pct) || (baseAmount > 0 ? roundToTwoDecimals((totalDiscountAmount / baseAmount) * 100) : 0);
  const subtotalAfterDiscounts = Math.max(baseAmount - totalDiscountAmount, 0);

  const discountRows = [
    {
      label: 'Plan discount',
      amount: planDiscountAmount,
      pct: toBreakupNumber(row.plan_discount_pct)
    },
    {
      label: 'Member discount',
      amount: memberDiscountAmount,
      pct: toBreakupNumber(row.member_discount_pct),
      note: row.member_discount_mode ? String(row.member_discount_mode).replace(/_/g, ' ') : 'none'
    },
    {
      label: 'Partner discount',
      amount: partnerDiscountAmount,
      pct: toBreakupNumber(row.partner_discount_pct)
    }
  ];

  return (
    <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
      <article className="panel crud-modal price-breakup-modal">
        <h2>Price Breakup</h2>
        <div className="price-breakup">
          <section className="price-breakup-hero" aria-label="Amount due">
            <div>
              <span>Amount to collect</span>
              <strong>{formatMoney(currency, dueAmount)}</strong>
            </div>
            <span className="price-breakup-hero__badge">
              {totalDiscountAmount > 0 ? `${formatMoney(currency, totalDiscountAmount)} saved` : 'No discount'}
            </span>
          </section>

          <section className="price-breakup-lines" aria-label="Calculation">
            <div className="price-breakup-line">
              <span>Base amount</span>
              <strong>{formatMoney(currency, baseAmount)}</strong>
            </div>
            <div className="price-breakup-line price-breakup-line--saving">
              <span>Total discount <small>{totalDiscountPct}%</small></span>
              <strong>-{formatMoney(currency, totalDiscountAmount)}</strong>
            </div>
            <div className="price-breakup-line">
              <span>Subtotal</span>
              <strong>{formatMoney(currency, subtotalAfterDiscounts)}</strong>
            </div>
            <div className="price-breakup-line">
              <span>Signup fee</span>
              <strong>{formatMoney(currency, signupFee)}</strong>
            </div>
            <div className="price-breakup-line price-breakup-line--total">
              <span>Due amount</span>
              <strong>{formatMoney(currency, dueAmount)}</strong>
            </div>
          </section>

          <section className="price-breakup-discounts" aria-label="Discount details">
            {discountRows.map((item) => (
              <div className={item.amount > 0 ? 'price-breakup-discount' : 'price-breakup-discount is-empty'} key={item.label}>
                <span>{item.label}</span>
                <strong>{item.amount > 0 ? `-${formatMoney(currency, item.amount)}` : formatMoney(currency, 0)}</strong>
                <small>{item.pct}%{item.note ? ` - ${item.note}` : ''}</small>
              </div>
            ))}
          </section>
        </div>
        <div className="row-actions">
          <button className="btn-link btn-cancel" type="button" onClick={onClose}>
            <CloseOutlinedIcon fontSize="small" />Close
          </button>
        </div>
      </article>
    </div>
  );
};

const clampPercentInput = (value) => {
  if (value === '') {
    return '';
  }
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return '';
  }
  return formatCalculatedInput(Math.min(Math.max(numberValue, 0), 100));
};

const clampAmountInput = (value) => {
  if (value === '') {
    return '';
  }
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return '';
  }
  return formatCalculatedInput(Math.max(numberValue, 0));
};

const calculateDiscountAmountFromPercent = (charge, discountPct) => {
  const chargeValue = Number(charge);
  const pctValue = Number(discountPct);
  if (!Number.isFinite(chargeValue) || chargeValue <= 0 || !Number.isFinite(pctValue)) {
    return '';
  }
  return formatCalculatedInput((chargeValue * pctValue) / 100);
};

const calculateDiscountPercentFromAmount = (charge, discountAmount) => {
  const chargeValue = Number(charge);
  const amountValue = Number(discountAmount);
  if (!Number.isFinite(chargeValue) || chargeValue <= 0 || !Number.isFinite(amountValue)) {
    return '';
  }
  return clampPercentInput((amountValue / chargeValue) * 100);
};

const clampDiscountAmountForCharge = (charge, discountAmount) => {
  const amountText = clampAmountInput(discountAmount);
  if (amountText === '') {
    return '';
  }
  const chargeValue = Number(charge);
  const amountValue = Number(amountText);
  if (!Number.isFinite(chargeValue) || chargeValue <= 0) {
    return amountText;
  }
  return formatCalculatedInput(Math.min(amountValue, chargeValue));
};

const getDurationMonthsError = (value, { required = true } = {}) => {
  if (value === '' || value == null) {
    return required ? 'Duration months is required.' : '';
  }
  const durationValue = Number(value);
  if (!Number.isFinite(durationValue) || durationValue <= 0) {
    return 'Set the duration for at least one month.';
  }
  if (!Number.isInteger(durationValue)) {
    return 'Duration months must be a whole number, such as 1, 3, 6, or 12.';
  }
  return '';
};

const setDurationMonthsValidity = (event) => {
  event.target.setCustomValidity('Set the duration for at least one month. Use whole months only.');
};

const clearDurationMonthsValidity = (event) => {
  event.target.setCustomValidity('');
};

const normalizeBillingTier = (tier) => String(tier || '').trim().toLowerCase();

const getBillingTierLabel = (tier) => {
  const normalized = normalizeBillingTier(tier);
  if (normalized === 'starter') {
    return 'Starter';
  }
  if (normalized === 'growth') {
    return 'Growth';
  }
  return 'Not configured';
};

const getBillingAlertAccess = (tier) => {
  const normalized = normalizeBillingTier(tier);
  return {
    tier: normalized,
    hasManualAlerts: false,
    hasAutomatedAlerts: normalized === 'growth',
    hasDiscontinueEmail: normalized === 'growth',
    hasAnyAlerts: normalized === 'growth'
  };
};

const GROWTH_ALERT_STAGES = [
  { key: 'automated_before_due_5', label: '5 days before', bucket: 'before' },
  { key: 'automated_before_due_3', label: '3 days before', bucket: 'before' },
  { key: 'automated_before_due_1', label: '1 day before', bucket: 'before' },
  { key: 'automated_due', label: 'Due date', bucket: 'due' },
  { key: 'automated_overdue_1', label: '1 day after', bucket: 'after' },
  { key: 'automated_overdue_2', label: '2 days after', bucket: 'after' }
];

const ALERT_TYPE_LABELS = GROWTH_ALERT_STAGES.reduce((labels, stage) => {
  labels[stage.key] = stage.label;
  return labels;
}, {
  overdue_manual: 'Discontinuation email'
});

const addDaysToDateInput = (value, days) => {
  const base = value ? new Date(value) : null;
  if (!base || Number.isNaN(base.getTime())) return '';
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
};

const toScheduledIso = (dateValue) => {
  const date = dateValue ? new Date(`${dateValue}T09:00:00`) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
};

function PartnerDashboard() {
  const dispatch = useDispatch();
  const { partnerId, partnerName, outlets, members, plans, outletPlansByOutlet, outletSubscriptions, billingSummary, alertQueue, planCharges, selectedOutletId, emailVerificationRequired, status, actionStatus, error } =
    useSelector((state) => state.partnerWorkspace);
  const [activeMenu, setActiveMenu] = useState('Home');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [planModalType, setPlanModalType] = useState(null);
  const [planActionConfirm, setPlanActionConfirm] = useState(null);
  const [memberDeleteConfirm, setMemberDeleteConfirm] = useState(null);
  const [outletSwitcherOpen, setOutletSwitcherOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [outletFilterStatus, setOutletFilterStatus] = useState('ALL');
  const [outletFilterCity, setOutletFilterCity] = useState('ALL');
  const [planDiscountSource, setPlanDiscountSource] = useState('percent');
  const [editingPlanDiscountSource, setEditingPlanDiscountSource] = useState('percent');

  const [planForm, setPlanForm] = useState({
    name: '',
    charge: '',
    duration_months: '',
    description: '',
    discount_pct: '',
    discount_amount: '',
    trainer_support: false,
    currency: 'INR'
  });

  const [memberStep, setMemberStep] = useState(1);
  const [createdMemberId, setCreatedMemberId] = useState('');
  const [memberOtp, setMemberOtp] = useState({ email: '' });
  const [memberOtpVerified, setMemberOtpVerified] = useState({ email: false });
  const [memberDocuments, setMemberDocuments] = useState([]);
  const [skipMemberDocumentUpload, setSkipMemberDocumentUpload] = useState(false);
  const [memberDocType, setMemberDocType] = useState('kyc');
  const [memberAvailablePlans, setMemberAvailablePlans] = useState([]);
  const [memberSelectedPlanId, setMemberSelectedPlanId] = useState('');
  const [memberPlanStartDate, setMemberPlanStartDate] = useState('');
  const [memberProfilePhoto, setMemberProfilePhoto] = useState(null);
  const [memberProfilePreview, setMemberProfilePreview] = useState('');
  const [profileCameraModal, setProfileCameraModal] = useState({ open: false, error: '' });
  const [memberPaymentQuote, setMemberPaymentQuote] = useState(null);
  const [memberPaymentIntent, setMemberPaymentIntent] = useState(null);
  const [memberPaymentConfirmed, setMemberPaymentConfirmed] = useState(false);
  const [cashPaymentConfirmOpen, setCashPaymentConfirmOpen] = useState(false);
  const [memberForm, setMemberForm] = useState({
    outlet_id: '',
    name: '',
    email: '',
    mobile: '',
    country: '',
    city: '',
    state: '',
    address: '',
    pincode: '',
    weight: '',
    weight_unit: 'kg',
    height: '',
    height_unit: 'cm',
    height_feet: '',
    height_inches: '',
    other_info: '',
    signup_fee_override: '',
    signup_fee_exempt: false,
    member_discount_mode: 'none',
    member_discount_pct: ''
  });
  const [showMemberOnboarding, setShowMemberOnboarding] = useState(false);
  const [memberFilterStatus, setMemberFilterStatus] = useState('ALL');
  const [memberSortOrder, setMemberSortOrder] = useState('newest');
  const [editingMemberStep, setEditingMemberStep] = useState(1);
  const [editingMemberDocType, setEditingMemberDocType] = useState('kyc');
  const [editingMemberDocuments, setEditingMemberDocuments] = useState([]);
  const [editingMemberAvailablePlans, setEditingMemberAvailablePlans] = useState([]);
  const [editingMemberSelectedPlanId, setEditingMemberSelectedPlanId] = useState('');
  const [editingMemberPlanStartDate, setEditingMemberPlanStartDate] = useState('');
  const [billingTab, setBillingTab] = useState('NEXT_PAYMENTS');
  const [selectedRenewalId, setSelectedRenewalId] = useState('');
  const [renewalMemberSearch, setRenewalMemberSearch] = useState('');
  const [renewalPlanOptions, setRenewalPlanOptions] = useState([]);
  const [renewalSelectedPlanId, setRenewalSelectedPlanId] = useState('');
  const [renewalPlanStartDate, setRenewalPlanStartDate] = useState(todayDateInputValue);
  const [renewalPartnerDiscountPct, setRenewalPartnerDiscountPct] = useState('');
  const [renewalPartnerDiscountAmount, setRenewalPartnerDiscountAmount] = useState('');
  const [renewalPartnerDiscountSource, setRenewalPartnerDiscountSource] = useState('percent');
  const [renewalQuote, setRenewalQuote] = useState(null);
  const [renewalQuoteStatus, setRenewalQuoteStatus] = useState('idle');
  const [billingMemberFilter, setBillingMemberFilter] = useState('');
  const [billingDueFilter, setBillingDueFilter] = useState('NEXT_15_DAYS');
  const [overdueSort, setOverdueSort] = useState({ key: 'daysOverdue', direction: 'desc' });
  // Retained only for backwards-compatible state restoration; no manual-alert UI is rendered.
  const [manualAlertModal, setManualAlertModal] = useState({ open: false, row: null, mode: 'email', alertType: '', scheduledDate: '', message: '' });
  const [discontinueEmailModal, setDiscontinueEmailModal] = useState({ open: false, row: null, sending: false, error: '' });
  const [sentDiscontinueEmailRows, setSentDiscontinueEmailRows] = useState([]);
  const [alertQueueStatusTab, setAlertQueueStatusTab] = useState('QUEUED');
  const [markPaidModal, setMarkPaidModal] = useState({ open: false, row: null, transactionId: '' });
  const [dueHistoryModal, setDueHistoryModal] = useState({ open: false, row: null, history: [] });
  const [breakupModal, setBreakupModal] = useState({ open: false, row: null });
  const [upiModal, setUpiModal] = useState({ open: false, row: null, upiUri: '', qrCodeUrl: '' });
  const [planLimitModal, setPlanLimitModal] = useState({ open: false, row: null });
  const [holdModal, setHoldModal] = useState({
    open: false,
    row: null,
    holdMonths: 1,
    holdingChargePerMonth: ''
  });
  const [resumeHoldModal, setResumeHoldModal] = useState({
    open: false,
    row: null,
    amount: '',
    method: 'UPI',
    transactionId: ''
  });
  const [outletSignupFeeDrafts, setOutletSignupFeeDrafts] = useState({});
  const prevActionStatusRef = useRef(actionStatus);
  const prevErrorRef = useRef(error);
  const profileVideoRef = useRef(null);
  const profileCanvasRef = useRef(null);
  const profileFileInputRef = useRef(null);

  const updatePlanCharge = (charge) => {
    setPlanForm((p) => {
      const next = { ...p, charge };
      if (planDiscountSource === 'amount') {
        next.discount_amount = clampDiscountAmountForCharge(charge, p.discount_amount);
        next.discount_pct = next.discount_amount === '' ? '' : calculateDiscountPercentFromAmount(charge, next.discount_amount);
      } else {
        next.discount_amount = p.discount_pct === '' ? '' : calculateDiscountAmountFromPercent(charge, p.discount_pct);
      }
      return next;
    });
  };

  const updatePlanDiscountPercent = (discountPct) => {
    setPlanDiscountSource('percent');
    setPlanForm((p) => {
      const nextPct = clampPercentInput(discountPct);
      return {
        ...p,
        discount_pct: nextPct,
        discount_amount: nextPct === '' ? '' : calculateDiscountAmountFromPercent(p.charge, nextPct)
      };
    });
  };

  const updatePlanDiscountAmount = (discountAmount) => {
    setPlanDiscountSource('amount');
    setPlanForm((p) => {
      const nextAmount = clampDiscountAmountForCharge(p.charge, discountAmount);
      return {
        ...p,
        discount_amount: nextAmount,
        discount_pct: nextAmount === '' ? '' : calculateDiscountPercentFromAmount(p.charge, nextAmount)
      };
    });
  };

  const updateEditingPlanCharge = (charge) => {
    setEditingPlan((p) => {
      if (!p) {
        return p;
      }
      const next = { ...p, charge };
      if (editingPlanDiscountSource === 'amount') {
        next.discount_amount = clampDiscountAmountForCharge(charge, p.discount_amount);
        next.discount_pct = next.discount_amount === '' ? '' : calculateDiscountPercentFromAmount(charge, next.discount_amount);
      } else {
        next.discount_amount = p.discount_pct === '' ? '' : calculateDiscountAmountFromPercent(charge, p.discount_pct);
      }
      return next;
    });
  };

  const updateEditingPlanDiscountPercent = (discountPct) => {
    setEditingPlanDiscountSource('percent');
    setEditingPlan((p) => {
      if (!p) {
        return p;
      }
      const nextPct = clampPercentInput(discountPct);
      return {
        ...p,
        discount_pct: nextPct,
        discount_amount: nextPct === '' ? '' : calculateDiscountAmountFromPercent(p.charge, nextPct)
      };
    });
  };

  const updateEditingPlanDiscountAmount = (discountAmount) => {
    setEditingPlanDiscountSource('amount');
    setEditingPlan((p) => {
      if (!p) {
        return p;
      }
      const nextAmount = clampDiscountAmountForCharge(p.charge, discountAmount);
      return {
        ...p,
        discount_amount: nextAmount,
        discount_pct: nextAmount === '' ? '' : calculateDiscountPercentFromAmount(p.charge, nextAmount)
      };
    });
  };
  const profileMediaStreamRef = useRef(null);
  const outletSwitcherRef = useRef(null);

  useEffect(() => {
    dispatch(bootstrapPartnerWorkspace());
  }, [dispatch]);

  useEffect(() => {
    const closeOutletSwitcher = (event) => {
      if (outletSwitcherRef.current && !outletSwitcherRef.current.contains(event.target)) {
        setOutletSwitcherOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOutletSwitcherOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOutletSwitcher);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutletSwitcher);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (outlets.length > 0) {
      dispatch(fetchPlansForOutlets(outlets));
    }
  }, [dispatch, outlets]);

  useEffect(() => {
    if (partnerId) {
      dispatch(fetchPartnerSubscriptions(partnerId));
      dispatch(fetchAlertQueue());
    }
  }, [dispatch, partnerId]);

  useEffect(() => {
    if (selectedOutletId && String(memberForm.outlet_id) !== String(selectedOutletId)) {
      setMemberForm((prev) => ({ ...prev, outlet_id: String(selectedOutletId) }));
    }
  }, [selectedOutletId, memberForm.outlet_id]);

  const selectedOutlet = useMemo(
    () => outlets.find((o) => String(o.id) === String(selectedOutletId)),
    [outlets, selectedOutletId]
  );
  const selectedOutletLabel = selectedOutlet?.name || 'Selected Outlet';
  const partnerDisplayName = partnerName || 'your account';
  const normalizedGlobalSearch = globalSearch.trim().toLowerCase();
  const matchesGlobalSearch = useCallback((...values) => {
    if (!normalizedGlobalSearch) {
      return true;
    }
    return values.some((value) => String(value || '').toLowerCase().includes(normalizedGlobalSearch));
  }, [normalizedGlobalSearch]);

  const selectedMemberOutlet = useMemo(
    () => outlets.find((o) => String(o.id) === String(memberForm.outlet_id)),
    [outlets, memberForm.outlet_id]
  );
  const outletSignupFeeDefault = Number(selectedMemberOutlet?.signup_fee_default || 0);
  const invoiceCurrency = memberPaymentIntent?.currency || memberPaymentQuote?.currency || 'INR';
  const invoiceTotal = Number(memberPaymentQuote?.final_amount ?? memberPaymentIntent?.amount ?? 0);
  const invoiceDateLabel = useMemo(() => new Date().toLocaleDateString(), []);
  const selectedPlanLabel = memberPaymentQuote?.plan_name || 'Plan';
  const selectedMemberName = memberPaymentQuote?.member_name || memberForm.name || 'Member';
  const planStartLabel = memberPlanStartDate ? new Date(memberPlanStartDate).toLocaleDateString() : 'Immediate';

  const getLocalAvailablePlansForOutlet = (outletId) => {
    const outletIdText = String(outletId || '');
    const localPlans = outletPlansByOutlet?.[outletIdText] || (String(selectedOutletId) === outletIdText ? plans : []);
    return normalizeAvailablePlans(localPlans).filter((record) => record.active !== false && record.plan?.active !== false);
  };

  const loadAvailablePlansForOutlet = async (outletId) => {
    if (!outletId) {
      return [];
    }

    const plansResult = await dispatch(fetchAvailablePlansForMember(outletId));
    if (fetchAvailablePlansForMember.fulfilled.match(plansResult)) {
      const apiPlans = normalizeAvailablePlans(plansResult.payload || []);
      if (apiPlans.length > 0) {
        return apiPlans;
      }
    }

    const localPlans = getLocalAvailablePlansForOutlet(outletId);
    if (localPlans.length > 0) {
      return localPlans;
    }

    const allPlansResult = await dispatch(fetchPlansByOutlet(outletId));
    if (fetchPlansByOutlet.fulfilled.match(allPlansResult)) {
      return normalizeAvailablePlans(allPlansResult.payload?.plans || []).filter(
        (record) => record.active !== false && record.plan?.active !== false
      );
    }

    return [];
  };

  useEffect(() => {
    if (partnerId) {
      dispatch(fetchBillingDashboard(partnerId));
    }
  }, [activeMenu, partnerId, dispatch]);

  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      showNotification('error', 'topRight', 'Action failed', String(error));
    }
    prevErrorRef.current = error;
  }, [error]);

  useEffect(() => {
    if (prevActionStatusRef.current !== 'succeeded' && actionStatus === 'succeeded') {
      showNotification('success', 'topRight', 'Success', 'Operation completed successfully.');
    }
    prevActionStatusRef.current = actionStatus;
  }, [actionStatus]);

  const outletCities = useMemo(() => {
    return Array.from(new Set(outlets.map((outlet) => String(outlet?.city || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [outlets]);

  const memberCountsByOutletId = useMemo(() => {
    const counts = {};
    members.forEach((member) => {
      const outletId = String(member?.outlet_id || '').trim();
      if (!outletId) {
        return;
      }
      counts[outletId] = (counts[outletId] || 0) + 1;
    });
    return counts;
  }, [members]);

  const filteredOutlets = useMemo(() => {
    return outlets.filter((outlet) => {
      const hasGlobalMatch = !normalizedGlobalSearch || (
        (outlet?.name || outlet?.country || outlet?.city || outlet?.state || outlet?.address || outlet?.pincode) &&
        matchesGlobalSearch(outlet.name, outlet.country, outlet.city, outlet.state, outlet.address, outlet.pincode)
      );

      if (!hasGlobalMatch) {
        return false;
      }

      if (outletFilterStatus && outletFilterStatus !== 'ALL') {
        const statusLabel = outlet?.is_active ? 'Active' : 'Inactive';
        if (String(statusLabel || '').toUpperCase() !== String(outletFilterStatus || '').toUpperCase()) {
          return false;
        }
      }

      if (outletFilterCity && outletFilterCity !== 'ALL') {
        if (!String(outlet?.city || '').toLowerCase().includes(String(outletFilterCity || '').toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [outlets, normalizedGlobalSearch, matchesGlobalSearch, outletFilterStatus, outletFilterCity]);

  const filteredPlans = useMemo(() => {
    if (!normalizedGlobalSearch) {
      return plans;
    }
    return plans.filter((record) => matchesGlobalSearch(
      record?.plan?.name,
      record?.plan?.description,
      record?.plan?.currency,
      record?.active ? 'active' : 'inactive'
    ));
  }, [plans, normalizedGlobalSearch, matchesGlobalSearch]);

  const filteredMembers = useMemo(() => {
    if (!selectedOutletId) {
      return [];
    }

    return [...members]
      .filter((member) => {
        const outletMatches = String(member.outlet_id) === String(selectedOutletId);
        if (!outletMatches) {
          return false;
        }
        if (memberFilterStatus && memberFilterStatus !== 'ALL') {
          const statusLabel = member?.is_active ? 'Active' : 'Inactive';
          if (String(statusLabel || '').toUpperCase() !== String(memberFilterStatus || '').toUpperCase()) {
            return false;
          }
        }
        return matchesGlobalSearch(member.name, member.email, member.mobile, member.outlet_name, member.is_active ? 'active' : 'inactive');
      })
      .sort((a, b) => {
        const createdValueA = a?.created_on || a?.created_at || a?.onboarding_date || a?.onboarded_on;
        const createdValueB = b?.created_on || b?.created_at || b?.onboarding_date || b?.onboarded_on;
        const timeA = createdValueA ? new Date(createdValueA).getTime() : 0;
        const timeB = createdValueB ? new Date(createdValueB).getTime() : 0;
        return memberSortOrder === 'oldest' ? timeA - timeB : timeB - timeA;
      });
  }, [members, selectedOutletId, matchesGlobalSearch, memberFilterStatus, memberSortOrder]);

  const dueWindowEndDate = useMemo(() => {
    if (billingDueFilter === 'ALL') {
      return null;
    }
    const now = new Date();
    const end = new Date(now);
    if (billingDueFilter === 'NEXT_5_DAYS') {
      end.setDate(end.getDate() + 5);
    } else if (billingDueFilter === 'NEXT_10_DAYS') {
      end.setDate(end.getDate() + 10);
    } else if (billingDueFilter === 'NEXT_15_DAYS') {
      end.setDate(end.getDate() + 15);
    }
    return end;
  }, [billingDueFilter]);

  const baseBillingFilteredRows = useMemo(() => {
    const filterBySharedCriteria = (rows = []) =>
      rows.filter((row) => {
        const rowOutletId = row?.outlet_id ? String(row.outlet_id) : '';
        if (selectedOutletId && rowOutletId && String(rowOutletId) !== String(selectedOutletId)) {
          return false;
        }
        if (billingMemberFilter.trim()) {
          const needle = billingMemberFilter.trim().toLowerCase();
          if (!(row?.member_name || '').toLowerCase().includes(needle)) {
            return false;
          }
        }
        return true;
      });

    const nextRows = filterBySharedCriteria(billingSummary?.next_payments || []).filter((row) => {
      if (!row?.due_date) {
        return false;
      }
      if (!dueWindowEndDate) {
        return true;
      }
      const now = new Date();
      // Reset time to midnight for date-only comparison
      now.setHours(0, 0, 0, 0);
      const due = new Date(row.due_date);
      due.setHours(0, 0, 0, 0);
      const windowEnd = new Date(dueWindowEndDate);
      windowEnd.setHours(0, 0, 0, 0);
      return due >= now && due <= windowEnd;
    });

    const overdueRows = filterBySharedCriteria(billingSummary?.overdue_payments || []).map((row) => {
      const dueDate = row?.due_date ? new Date(row.due_date) : null;
      const daysOverdue = dueDate ? Math.max(Math.ceil((Date.now() - dueDate.getTime()) / (1000 * 60 * 60 * 24)), 0) : 0;
      return { ...row, daysOverdue };
    });

    const historyRows = filterBySharedCriteria(billingSummary?.payment_history || []);
    const onHoldRows = filterBySharedCriteria(billingSummary?.on_hold_payments || []);

    return { nextRows, overdueRows, historyRows, onHoldRows };
  }, [billingSummary, selectedOutletId, billingMemberFilter, dueWindowEndDate]);

  const filteredNextPayments = baseBillingFilteredRows.nextRows;
  const filteredPaymentHistory = baseBillingFilteredRows.historyRows;
  const filteredOnHoldMembers = baseBillingFilteredRows.onHoldRows;

  const renewalBaseRows = useMemo(() => {
    const combinedRows = [
      ...(billingSummary?.overdue_payments || []),
      ...(billingSummary?.next_payments || [])
    ];
    const uniqueRows = new Map();

    combinedRows.forEach((row) => {
      if (!row?.user_id || !row?.plan_id) {
        return;
      }
      const rowOutletId = row?.outlet_id ? String(row.outlet_id) : '';
      if (selectedOutletId && rowOutletId && String(rowOutletId) !== String(selectedOutletId)) {
        return;
      }
      uniqueRows.set(getRenewalRowId(row), row);
    });

    return Array.from(uniqueRows.values())
      .map((row) => {
        const daysUntilDue = getDayDifferenceFromToday(row?.due_date);
        const urgencyScore = daysUntilDue == null ? 9999 : daysUntilDue;
        return {
          ...row,
          daysUntilDue,
          renewalStatus: urgencyScore < 0 ? 'overdue' : (urgencyScore <= 7 ? 'due-soon' : 'active')
        };
      })
      .sort((a, b) => {
        const scoreA = a.daysUntilDue == null ? 9999 : a.daysUntilDue;
        const scoreB = b.daysUntilDue == null ? 9999 : b.daysUntilDue;
        return scoreA - scoreB;
      });
  }, [billingSummary?.next_payments, billingSummary?.overdue_payments, selectedOutletId]);

  const renewalCandidates = useMemo(() => {
    const needle = renewalMemberSearch.trim().toLowerCase();
    if (!needle) {
      return renewalBaseRows;
    }
    return renewalBaseRows.filter((row) => [
      row?.member_name,
      row?.mobile,
      row?.email,
      row?.plan_name,
      row?.outlet_name
    ].some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [renewalBaseRows, renewalMemberSearch]);

  useEffect(() => {
    if (renewalCandidates.length === 0) {
      if (selectedRenewalId) {
        setSelectedRenewalId('');
      }
      return;
    }
    const stillExists = renewalCandidates.some((row) => getRenewalRowId(row) === selectedRenewalId);
    if (!stillExists) {
      setSelectedRenewalId(getRenewalRowId(renewalCandidates[0]));
    }
  }, [renewalCandidates, selectedRenewalId]);

  const selectedRenewal = useMemo(() => {
    if (!renewalCandidates.length) {
      return null;
    }
    return renewalCandidates.find((row) => getRenewalRowId(row) === selectedRenewalId) || renewalCandidates[0];
  }, [renewalCandidates, selectedRenewalId]);

  useEffect(() => {
    let ignore = false;

    const loadRenewalPlans = async () => {
      if (!selectedRenewal?.outlet_id) {
        setRenewalPlanOptions([]);
        setRenewalSelectedPlanId('');
        return;
      }

      const availablePlans = await loadAvailablePlansForOutlet(selectedRenewal.outlet_id);
      if (ignore) {
        return;
      }
      setRenewalPlanOptions(availablePlans);
      const currentPlanId = String(selectedRenewal.plan_id || '');
      const currentPlanExists = availablePlans.some((record) => String(record?.plan_id || '') === currentPlanId);
      setRenewalSelectedPlanId(currentPlanExists ? currentPlanId : (availablePlans[0]?.plan_id ? String(availablePlans[0].plan_id) : currentPlanId));
    };

    loadRenewalPlans();
    return () => {
      ignore = true;
    };
  }, [selectedRenewal?.outlet_id, selectedRenewal?.plan_id]);

  useEffect(() => {
    setRenewalPlanStartDate(todayDateInputValue());
    setRenewalPartnerDiscountPct('');
    setRenewalPartnerDiscountAmount('');
    setRenewalPartnerDiscountSource('percent');
  }, [selectedRenewal?.user_id]);

  useEffect(() => {
    setRenewalPartnerDiscountPct('');
    setRenewalPartnerDiscountAmount('');
    setRenewalPartnerDiscountSource('percent');
  }, [renewalSelectedPlanId]);

  useEffect(() => {
    let ignore = false;

    const loadRenewalQuote = async () => {
      if (!selectedRenewal?.user_id || !renewalSelectedPlanId) {
        setRenewalQuote(null);
        setRenewalQuoteStatus('idle');
        return;
      }

      setRenewalQuoteStatus('loading');
      const quoteResult = await dispatch(fetchMemberPaymentQuote({
        memberId: selectedRenewal.user_id,
        planId: renewalSelectedPlanId
      }));
      if (ignore) {
        return;
      }
      if (fetchMemberPaymentQuote.fulfilled.match(quoteResult)) {
        setRenewalQuote(quoteResult.payload);
        setRenewalQuoteStatus('ready');
      } else {
        setRenewalQuote(null);
        setRenewalQuoteStatus('error');
      }
    };

    loadRenewalQuote();
    return () => {
      ignore = true;
    };
  }, [dispatch, renewalSelectedPlanId, selectedRenewal?.user_id]);

  const selectedRenewalPreviousPurchase = useMemo(() => {
    if (!selectedRenewal?.user_id) {
      return null;
    }
    return (billingSummary?.payment_history || [])
      .filter((row) => String(row?.user_id || '') === String(selectedRenewal.user_id))
      .sort((a, b) => new Date(b?.date_time || 0).getTime() - new Date(a?.date_time || 0).getTime())[0] || null;
  }, [billingSummary?.payment_history, selectedRenewal]);

  const selectedRenewalPreviousPurchaseDetails = useMemo(() => {
    if (!selectedRenewalPreviousPurchase) {
      return null;
    }
    return {
      planName: selectedRenewalPreviousPurchase.plan_name || selectedRenewal?.plan_name || 'Plan',
      finalAmount: Number(selectedRenewalPreviousPurchase.amount ?? selectedRenewalPreviousPurchase.due_amount ?? 0) || 0,
      baseAmount: Number(selectedRenewalPreviousPurchase.base_amount ?? 0) || 0,
      signupFee: Number(selectedRenewalPreviousPurchase.signup_fee ?? 0) || 0,
      planDiscountAmount: Number(selectedRenewalPreviousPurchase.plan_discount_amount ?? 0) || 0,
      memberDiscountAmount: Number(selectedRenewalPreviousPurchase.member_discount_amount ?? 0) || 0
    };
  }, [selectedRenewalPreviousPurchase, selectedRenewal]);

  const selectedRenewalPlan = useMemo(() => {
    if (!renewalSelectedPlanId) {
      return null;
    }
    return renewalPlanOptions.find((record) => String(record?.plan_id || record?.plan?.id || '') === String(renewalSelectedPlanId)) || null;
  }, [renewalPlanOptions, renewalSelectedPlanId]);

  const renewalComputedQuote = useMemo(() => {
    if (!selectedRenewal) {
      return null;
    }
    if (renewalQuote && String(renewalQuote.plan_id || '') === String(renewalSelectedPlanId || selectedRenewal.plan_id || '')) {
      return renewalQuote;
    }
    return calculateRenewalPlanBreakup(selectedRenewalPlan, selectedRenewal);
  }, [renewalQuote, renewalSelectedPlanId, selectedRenewal, selectedRenewalPlan]);

  const renewalAmountAfterStandardDiscounts = useMemo(() => {
    if (!renewalComputedQuote) {
      return 0;
    }
    return Number(renewalComputedQuote.final_amount ?? 0) || 0;
  }, [renewalComputedQuote]);

  const updateRenewalPartnerDiscountPercent = (discountPct) => {
    setRenewalPartnerDiscountSource('percent');
    const nextPct = clampPercentInput(discountPct);
    setRenewalPartnerDiscountPct(nextPct);
    setRenewalPartnerDiscountAmount(
      nextPct === '' ? '' : calculateDiscountAmountFromPercent(renewalAmountAfterStandardDiscounts, nextPct)
    );
  };

  const updateRenewalPartnerDiscountAmount = (discountAmount) => {
    setRenewalPartnerDiscountSource('amount');
    const nextAmount = clampDiscountAmountForCharge(renewalAmountAfterStandardDiscounts, discountAmount);
    setRenewalPartnerDiscountAmount(nextAmount);
    setRenewalPartnerDiscountPct(
      nextAmount === '' ? '' : calculateDiscountPercentFromAmount(renewalAmountAfterStandardDiscounts, nextAmount)
    );
  };

  useEffect(() => {
    if (renewalPartnerDiscountSource === 'amount') {
      const nextAmount = clampDiscountAmountForCharge(renewalAmountAfterStandardDiscounts, renewalPartnerDiscountAmount);
      setRenewalPartnerDiscountAmount(nextAmount);
      setRenewalPartnerDiscountPct(
        nextAmount === '' ? '' : calculateDiscountPercentFromAmount(renewalAmountAfterStandardDiscounts, nextAmount)
      );
      return;
    }
    setRenewalPartnerDiscountAmount(
      renewalPartnerDiscountPct === ''
        ? ''
        : calculateDiscountAmountFromPercent(renewalAmountAfterStandardDiscounts, renewalPartnerDiscountPct)
    );
  }, [renewalAmountAfterStandardDiscounts]);

  const renewalCheckoutSummary = useMemo(() => {
    if (!selectedRenewal || !renewalComputedQuote) {
      return null;
    }
    const currency = renewalComputedQuote.currency || selectedRenewal.currency || 'INR';
    const baseAmount = Number(renewalComputedQuote.base_amount ?? 0) || 0;
    const standardSavingsAmount = Number(renewalComputedQuote.discount_amount ?? 0) || 0;
    const amountAfterStandardDiscounts = Number(renewalComputedQuote.final_amount ?? 0) || 0;
    const partnerDiscountAmount = renewalPartnerDiscountSource === 'amount'
      ? Math.min(Math.max(Number(renewalPartnerDiscountAmount || 0) || 0, 0), amountAfterStandardDiscounts)
      : Math.min(
        Math.round(((amountAfterStandardDiscounts * Math.min(Math.max(Number(renewalPartnerDiscountPct || 0) || 0, 0), 100)) / 100) * 100) / 100,
        amountAfterStandardDiscounts
      );
    const partnerDiscountPct = amountAfterStandardDiscounts > 0
      ? Math.round(((partnerDiscountAmount / amountAfterStandardDiscounts) * 100) * 100) / 100
      : 0;
    const finalAmount = Math.max(Math.round((amountAfterStandardDiscounts - partnerDiscountAmount) * 100) / 100, 0);
    const savingsAmount = Math.round((standardSavingsAmount + partnerDiscountAmount) * 100) / 100;
    const previousAmount = selectedRenewalPreviousPurchase
      ? Number(selectedRenewalPreviousPurchase.amount ?? 0) || 0
      : null;
    const priceDifference = previousAmount == null ? null : finalAmount - previousAmount;
    const durationMonths = Number(selectedRenewalPlan?.plan?.duration_months || selectedRenewal.plan_duration_months || 1) || 1;
    const planEndDate = calculatePlanEndDate(renewalPlanStartDate, durationMonths);
    const daysUntilDue = selectedRenewal.daysUntilDue;
    const currentStatus = selectedRenewal.renewalStatus === 'overdue'
      ? `${Math.abs(daysUntilDue || 0)} day${Math.abs(daysUntilDue || 0) === 1 ? '' : 's'} overdue`
      : (selectedRenewal.renewalStatus === 'due-soon'
        ? `Due in ${daysUntilDue} day${daysUntilDue === 1 ? '' : 's'}`
        : 'Active membership');
    const recommendation = selectedRenewal.renewalStatus === 'overdue'
      ? 'Collect payment and renew membership'
      : `Renew membership for ${durationMonths} month${durationMonths === 1 ? '' : 's'}`;

    return {
      currency,
      finalAmount,
      baseAmount,
      standardSavingsAmount,
      partnerDiscountAmount,
      partnerDiscountPct,
      savingsAmount,
      amountAfterStandardDiscounts,
      previousAmount,
      priceDifference,
      durationMonths,
      planEndDate,
      currentStatus,
      recommendation,
      newPlanName: renewalComputedQuote.plan_name || selectedRenewalPlan?.plan?.name || selectedRenewal.plan_name || 'Plan',
      signupFee: 0,
      planDiscountAmount: Number(renewalComputedQuote.plan_discount_amount || 0),
      planDiscountPct: Number(renewalComputedQuote.plan_discount_pct || 0),
      memberDiscountAmount: Number(renewalComputedQuote.member_discount_amount || 0),
      memberDiscountPct: Number(renewalComputedQuote.member_discount_pct || 0),
      memberDiscountMode: renewalComputedQuote.member_discount_mode || 'none'
    };
  }, [
    renewalComputedQuote,
    renewalPartnerDiscountAmount,
    renewalPartnerDiscountPct,
    renewalPartnerDiscountSource,
    renewalPlanStartDate,
    selectedRenewal,
    selectedRenewalPlan,
    selectedRenewalPreviousPurchase
  ]);

  const filteredOverduePayments = useMemo(() => {
    const rows = [...(baseBillingFilteredRows.overdueRows || [])];
    const { key, direction } = overdueSort;
    const factor = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (key === 'daysOverdue') {
        return ((a.daysOverdue || 0) - (b.daysOverdue || 0)) * factor;
      }
      if (key === 'amount') {
        return ((Number(a.amount) || 0) - (Number(b.amount) || 0)) * factor;
      }
      if (key === 'due_date') {
        return ((new Date(a.due_date || 0)).getTime() - (new Date(b.due_date || 0)).getTime()) * factor;
      }
      const aa = String(a?.[key] || '').toLowerCase();
      const bb = String(b?.[key] || '').toLowerCase();
      return aa.localeCompare(bb) * factor;
    });
    return rows;
  }, [baseBillingFilteredRows.overdueRows, overdueSort]);

  const billingOverview = useMemo(() => {
    const sumAmount = (rows, keys) => rows.reduce((total, row) => {
      const value = keys.reduce((acc, key) => (acc != null ? acc : row?.[key]), null);
      return total + (Number(value) || 0);
    }, 0);

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const collectedThisMonth = (filteredPaymentHistory || []).filter((row) => {
      if (!row?.date_time) return false;
      return new Date(row.date_time) >= monthStart;
    });

    const currency =
      filteredNextPayments[0]?.currency ||
      filteredOverduePayments[0]?.currency ||
      filteredPaymentHistory[0]?.currency ||
      'INR';

    return {
      currency,
      upcomingCount: filteredNextPayments.length,
      upcomingAmount: sumAmount(filteredNextPayments, ['due_amount', 'amount']),
      overdueCount: filteredOverduePayments.length,
      overdueAmount: sumAmount(filteredOverduePayments, ['amount', 'due_amount']),
      onHoldCount: filteredOnHoldMembers.length,
      collectedCount: collectedThisMonth.length,
      collectedAmount: sumAmount(collectedThisMonth, ['amount'])
    };
  }, [filteredNextPayments, filteredOverduePayments, filteredOnHoldMembers, filteredPaymentHistory]);

  const filteredAlertQueue = useMemo(() => {
    const desiredStatus = alertQueueStatusTab.toLowerCase();
    return (alertQueue || []).filter((item) => {
      const outletMatches = !selectedOutletId || String(item?.outlet_id || '') === String(selectedOutletId);
      const statusMatches = desiredStatus === 'all' || String(item?.delivery_status || '').toLowerCase() === desiredStatus;
      return outletMatches && statusMatches;
    });
  }, [alertQueue, selectedOutletId, alertQueueStatusTab]);

  const refreshAll = () => {
    dispatch(bootstrapPartnerWorkspace());
    dispatch(fetchAlertQueue());
  };

  const switchOutlet = (outletId) => {
    if (!outletId) {
      setOutletSwitcherOpen(false);
      return;
    }
    dispatch(setSelectedOutletId(outletId));
    dispatch(setSelectedOutletFilter(outletId));
    dispatch(fetchPlansByOutlet(outletId));
    setOutletSwitcherOpen(false);
  };

  const confirmPlanAction = async () => {
    if (!planActionConfirm?.plan?.id || !selectedOutletId) {
      return;
    }

    if (planActionConfirm.type === 'delete') {
      const result = await dispatch(deletePlan({ outletPlanId: planActionConfirm.plan.id, outletId: selectedOutletId }));
      if (deletePlan.fulfilled.match(result)) {
        setPlanActionConfirm(null);
      }
      return;
    }

    const result = await dispatch(
      togglePlan({
        outletPlanId: planActionConfirm.plan.id,
        activate: !planActionConfirm.plan.active,
        outletId: selectedOutletId
      })
    );
    if (togglePlan.fulfilled.match(result)) {
      setPlanActionConfirm(null);
    }
  };

  const confirmMemberDelete = async () => {
    if (!memberDeleteConfirm?.id) {
      return;
    }
    const result = await dispatch(deleteMember({ memberId: memberDeleteConfirm.id, partnerId }));
    if (deleteMember.fulfilled.match(result)) {
      setMemberDeleteConfirm(null);
    }
  };

  const submitPlan = async (event) => {
    event.preventDefault();
    if (!selectedOutletId) {
      return;
    }
    const durationError = getDurationMonthsError(planForm.duration_months);
    if (durationError) {
      showNotification('error', 'topRight', 'Invalid duration', durationError);
      return;
    }
    const payload = {
      outlet_id: selectedOutletId,
      name: planForm.name || null,
      charge: Number(planForm.charge),
      duration_months: Number(planForm.duration_months),
      description: planForm.description || null,
      discount_pct: planForm.discount_pct === '' ? null : Number(clampPercentInput(planForm.discount_pct)),
      discount_amount: planForm.discount_amount === '' ? null : Number(clampAmountInput(planForm.discount_amount)),
      trainer_support: !!planForm.trainer_support,
      currency: planForm.currency || 'INR'
    };
    await dispatch(addPlan(payload));
    setPlanForm({
      name: '',
      charge: '',
      duration_months: '',
      description: '',
      discount_pct: '',
      discount_amount: '',
      trainer_support: false,
      currency: 'INR'
    });
    setPlanDiscountSource('percent');
    setPlanModalType(null);
  };

  const submitEditPlan = async (event) => {
    event.preventDefault();
    if (!editingPlan || !selectedOutletId) {
      return;
    }
    const durationError = getDurationMonthsError(editingPlan.duration_months, { required: false });
    if (durationError) {
      showNotification('error', 'topRight', 'Invalid duration', durationError);
      return;
    }
    const payload = {
      name: editingPlan.name || null,
      charge: editingPlan.charge === '' ? null : Number(editingPlan.charge),
      duration_months: editingPlan.duration_months === '' ? null : Number(editingPlan.duration_months),
      description: editingPlan.description || null,
      discount_pct: editingPlan.discount_pct === '' ? null : Number(clampPercentInput(editingPlan.discount_pct)),
      discount_amount: editingPlan.discount_amount === '' ? null : Number(clampAmountInput(editingPlan.discount_amount)),
      trainer_support: !!editingPlan.trainer_support,
      currency: editingPlan.currency || null
    };
    const result = await dispatch(editPlan({ outletPlanId: editingPlan.id, payload, outletId: selectedOutletId }));
    if (editPlan.fulfilled.match(result)) {
      setEditingPlan(null);
      setPlanModalType(null);
    }
  };

  const startMemberOnboarding = () => {
    if (!selectedOutletId) {
      showNotification('error', 'topRight', 'Missing outlet', 'Select an outlet from the top selector before adding a member.');
      return;
    }
    setShowMemberOnboarding(true);
    setMemberStep(1);
    setCreatedMemberId('');
    setMemberOtp({ email: '' });
    setMemberOtpVerified({ email: false });
    setMemberDocuments([]);
    setSkipMemberDocumentUpload(false);
    setMemberDocType('kyc');
    setMemberAvailablePlans([]);
    setMemberSelectedPlanId('');
    setMemberPlanStartDate('');
    setMemberProfilePhoto(null);
    setMemberProfilePreview('');
    setMemberPaymentQuote(null);
    setMemberPaymentIntent(null);
    setMemberPaymentConfirmed(false);
    setMemberForm((prev) => ({
      ...prev,
      outlet_id: String(selectedOutletId),
      signup_fee_override: '',
      signup_fee_exempt: false,
      member_discount_mode: 'none',
      member_discount_pct: ''
    }));
  };

  const closeMemberOnboarding = async ({ skipCancel = false } = {}) => {
    const memberIdToCancel = createdMemberId;
    const shouldCancel = !!memberIdToCancel && !skipCancel && !memberPaymentConfirmed;
    setShowMemberOnboarding(false);
    setMemberStep(1);
    setCreatedMemberId('');
    setMemberOtp({ email: '' });
    setMemberOtpVerified({ email: false });
    setMemberDocuments([]);
    setSkipMemberDocumentUpload(false);
    setMemberDocType('kyc');
    setMemberAvailablePlans([]);
    setMemberSelectedPlanId('');
    setMemberPlanStartDate('');
    setMemberProfilePhoto(null);
    setMemberProfilePreview('');
    setMemberPaymentQuote(null);
    setMemberPaymentIntent(null);
    setMemberPaymentConfirmed(false);
    setMemberForm((prev) => ({
      ...prev,
      outlet_id: selectedOutletId ? String(selectedOutletId) : prev.outlet_id,
      signup_fee_override: '',
      signup_fee_exempt: false,
      member_discount_mode: 'none',
      member_discount_pct: ''
    }));
    if (profileMediaStreamRef.current) {
      profileMediaStreamRef.current.getTracks().forEach((track) => track.stop());
      profileMediaStreamRef.current = null;
    }
    setProfileCameraModal({ open: false, error: '' });
    if (shouldCancel) {
      await dispatch(cancelMemberOnboarding({ memberId: memberIdToCancel, partnerId }));
    }
  };

  const stopProfileCameraStream = () => {
    if (profileMediaStreamRef.current) {
      profileMediaStreamRef.current.getTracks().forEach((track) => track.stop());
      profileMediaStreamRef.current = null;
    }
  };

  const closeProfileCameraModal = () => {
    stopProfileCameraStream();
    setProfileCameraModal({ open: false, error: '' });
  };

  const openProfileCamera = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setProfileCameraModal({ open: false, error: 'Camera is not supported on this device/browser.' });
      return;
    }
    if (!window.isSecureContext) {
      setProfileCameraModal({ open: false, error: 'Camera needs a secure context (https or localhost).' });
      return;
    }
    try {
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'user' } },
          audio: false
        });
      } catch (firstError) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      profileMediaStreamRef.current = stream;
      setProfileCameraModal({ open: true, error: '' });
      setTimeout(() => {
        if (profileVideoRef.current) {
          profileVideoRef.current.srcObject = stream;
          profileVideoRef.current.play().catch(() => {});
        }
      }, 0);
    } catch (error) {
      const errorName = error?.name || '';
      let message = 'Unable to open camera.';
      if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
        message = 'Camera permission denied. Allow camera access in browser settings and try again.';
      } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
        message = 'No camera device found on this system.';
      } else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
        message = 'Camera is busy in another app. Close other camera apps and retry.';
      } else if (errorName === 'SecurityError') {
        message = 'Camera blocked by browser security policy. Use https or localhost.';
      }
      setProfileCameraModal({ open: false, error: message });
    }
  };

  const captureProfileFromCamera = async () => {
    const video = profileVideoRef.current;
    const canvas = profileCanvasRef.current;
    if (!video || !canvas) {
      return;
    }
    const width = video.videoWidth || 480;
    const height = video.videoHeight || 640;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) {
      return;
    }
    const file = new File([blob], `profile_${Date.now()}.jpg`, { type: 'image/jpeg' });
    setMemberProfilePhoto(file);
    if (memberProfilePreview) {
      URL.revokeObjectURL(memberProfilePreview);
    }
    setMemberProfilePreview(URL.createObjectURL(file));
    closeProfileCameraModal();
  };

  const onProfilePhotoChange = (event) => {
    const file = event.target.files?.[0] || null;
    setMemberProfilePhoto(file);
    if (!file) {
      if (memberProfilePreview) {
        URL.revokeObjectURL(memberProfilePreview);
      }
      setMemberProfilePreview('');
      return;
    }
    if (memberProfilePreview) {
      URL.revokeObjectURL(memberProfilePreview);
    }
    const previewUrl = URL.createObjectURL(file);
    setMemberProfilePreview(previewUrl);
  };

  useEffect(() => () => {
    if (memberProfilePreview) {
      URL.revokeObjectURL(memberProfilePreview);
    }
    stopProfileCameraStream();
  }, [memberProfilePreview]);

  const submitMemberStepOne = async (event) => {
    event.preventDefault();
    const normalizedMobile = String(memberForm.mobile || '').replace(/[\s()-]/g, '');
    if (!/^\+?[1-9]\d{7,14}$/.test(normalizedMobile)) {
      showNotification('error', 'topRight', 'Invalid mobile number', 'Enter a valid mobile number with 8 to 15 digits.');
      return;
    }
    const payload = {
      partner_id: partnerId,
      outlet_id: memberForm.outlet_id,
      name: memberForm.name,
      email: memberForm.email,
      mobile: normalizedMobile,
      country: memberForm.country || null,
      city: memberForm.city || null,
      state: memberForm.state || null,
      address: memberForm.address || null,
      pincode: memberForm.pincode || null,
      weight: Number(memberForm.weight),
      weight_unit: memberForm.weight_unit || 'kg',
      height: combineHeightInput(memberForm),
      height_unit: memberForm.height_unit || 'cm',
      other_info: memberForm.other_info || null,
      signup_fee_exempt: !!memberForm.signup_fee_exempt,
      signup_fee_override: memberForm.signup_fee_exempt
        ? null
        : (memberForm.signup_fee_override === '' ? null : Number(memberForm.signup_fee_override)),
      member_discount_mode: memberForm.member_discount_mode || 'none',
      member_discount_pct:
        (memberForm.member_discount_mode || 'none') === 'none'
          ? 0
          : (memberForm.member_discount_pct === '' ? 0 : Number(clampPercentInput(memberForm.member_discount_pct)))
    };
    const result = await dispatch(addMemberStepOne(payload));
    if (addMemberStepOne.fulfilled.match(result)) {
      const userId = result.payload?.id;
      if (userId) {
        if (memberProfilePhoto && memberForm.outlet_id) {
          const uploadResult = await dispatch(
            uploadMemberDocuments({
              files: [memberProfilePhoto],
              userId,
              outletId: memberForm.outlet_id,
              docType: 'profile_pic',
              partnerId
            })
          );
          if (!uploadMemberDocuments.fulfilled.match(uploadResult)) {
            return;
          }
        }
        setCreatedMemberId(String(userId));
        setMemberStep(2);
      }
    }
  };

  const submitMemberOtp = async () => {
    if (!createdMemberId || !memberOtp.email) {
      return;
    }
    const result = await dispatch(verifyMemberOtp({ user_id: createdMemberId, otp: memberOtp.email, medium: 'email' }));
    if (verifyMemberOtp.fulfilled.match(result)) {
      setMemberOtpVerified({ email: true });
      setMemberStep(3);
    }
  };

  const submitMemberStepThree = async (event) => {
    event.preventDefault();
    if (!createdMemberId || !memberForm.outlet_id) {
      return;
    }
    if (skipMemberDocumentUpload) {
      const availablePlans = await loadAvailablePlansForOutlet(memberForm.outlet_id);
      setMemberAvailablePlans(availablePlans);
      setMemberSelectedPlanId(availablePlans[0]?.plan_id ? String(availablePlans[0].plan_id) : '');
      setMemberStep(4);
      return;
    }
    if (memberDocuments.length === 0) {
      showNotification('error', 'topRight', 'Documents required', 'Upload member documents or select Upload later.');
      return;
    }
    const result = await dispatch(
      uploadMemberDocuments({
        files: memberDocuments,
        userId: createdMemberId,
        outletId: memberForm.outlet_id,
        docType: memberDocType,
        partnerId
      })
    );
    if (uploadMemberDocuments.fulfilled.match(result)) {
      const availablePlans = await loadAvailablePlansForOutlet(memberForm.outlet_id);
      setMemberAvailablePlans(availablePlans);
      setMemberSelectedPlanId(availablePlans[0]?.plan_id ? String(availablePlans[0].plan_id) : '');
      setMemberStep(4);
    }
  };

  const submitMemberStepFour = async (event) => {
    event.preventDefault();
    if (!createdMemberId || !memberSelectedPlanId) {
      return;
    }
    const result = await dispatch(
      assignMemberToPlan({
        userId: createdMemberId,
        planId: memberSelectedPlanId,
        startDate: memberPlanStartDate || null,
        partnerId
      })
    );
    if (assignMemberToPlan.fulfilled.match(result)) {
      const [quoteResult, paymentResult] = await Promise.all([
        dispatch(fetchMemberPaymentQuote({ memberId: createdMemberId, planId: memberSelectedPlanId })),
        dispatch(initiateMemberUpiPayment({ memberId: createdMemberId, planId: memberSelectedPlanId }))
      ]);
      if (fetchMemberPaymentQuote.fulfilled.match(quoteResult)) {
        setMemberPaymentQuote(quoteResult.payload);
      }
      if (initiateMemberUpiPayment.fulfilled.match(paymentResult)) {
        setMemberPaymentIntent(paymentResult.payload);
      }
      setMemberStep(5);
    }
  };

  const confirmMemberOnboardingPayment = async () => {
    const transactionId = memberPaymentIntent?.transaction_id || `cash-${Date.now()}`;
    const amount = Number(memberPaymentIntent?.amount ?? memberPaymentQuote?.final_amount ?? 0);
    if (!createdMemberId) {
      showNotification('error', 'topRight', 'Payment not ready', 'Member onboarding session is missing. Please restart onboarding.');
      return;
    }
    if (!Number.isFinite(amount) || amount < 0) {
      showNotification('error', 'topRight', 'Invalid amount', 'Unable to confirm payment because the invoice amount is invalid.');
      return;
    }
    const result = await dispatch(
      confirmMemberUpiPayment({
        memberId: createdMemberId,
        transactionId,
        amount,
        method: 'Cash',
        partnerId
      })
    );
    if (confirmMemberUpiPayment.fulfilled.match(result)) {
      setMemberPaymentConfirmed(true);
      await closeMemberOnboarding({ skipCancel: true });
    } else {
      showNotification('error', 'topRight', 'Payment not confirmed', result.payload || 'Unable to confirm cash payment.');
    }
  };

  const submitEditMember = async (event) => {
    event.preventDefault();
    if (!editingMember) {
      return;
    }
    const payload = {
      name: editingMember.name || null,
      email: editingMember.email || null,
      mobile: editingMember.mobile || null,
      stats: {
        weight: editingMember.weight === '' || editingMember.weight == null ? null : Number(editingMember.weight),
        weight_unit: editingMember.weight_unit || 'kg',
        height: combineHeightInput(editingMember),
        height_unit: editingMember.height_unit || 'cm',
        other_info: editingMember.other_info || null
      },
      signup_fee_exempt: editingMember.signup_fee_exempt ?? null,
      signup_fee_override:
        editingMember.signup_fee_exempt
          ? 0
          : (editingMember.signup_fee_override === '' || editingMember.signup_fee_override == null
            ? null
            : Number(editingMember.signup_fee_override)),
      member_discount_mode: editingMember.member_discount_mode || 'none',
      member_discount_pct:
        (editingMember.member_discount_mode || 'none') === 'none'
          ? 0
          : (editingMember.member_discount_pct === '' || editingMember.member_discount_pct == null
            ? 0
            : Number(clampPercentInput(editingMember.member_discount_pct)))
    };
    const result = await dispatch(editMember({ memberId: editingMember.id, payload, partnerId }));
    if (editMember.fulfilled.match(result)) {
      setEditingMemberStep(2);
    }
  };

  const submitEditMemberDocuments = async (event) => {
    event.preventDefault();
    if (!editingMember?.id || !editingMember?.outlet_id || editingMemberDocuments.length === 0) {
      return;
    }
    const result = await dispatch(
      uploadMemberDocuments({
        files: editingMemberDocuments,
        userId: editingMember.id,
        outletId: editingMember.outlet_id,
        docType: editingMemberDocType,
        partnerId
      })
    );
    if (uploadMemberDocuments.fulfilled.match(result)) {
      const availablePlans = await loadAvailablePlansForOutlet(editingMember.outlet_id);
      setEditingMemberAvailablePlans(availablePlans);
      setEditingMemberSelectedPlanId(availablePlans[0]?.plan_id ? String(availablePlans[0].plan_id) : '');
      setEditingMemberStep(3);
    }
  };

  const loadEditingMemberPlanOptions = async (member) => {
    if (!member?.outlet_id) {
      setEditingMemberAvailablePlans([]);
      setEditingMemberSelectedPlanId('');
      return;
    }
    const availablePlans = await loadAvailablePlansForOutlet(member.outlet_id);
    const currentPlanId = member.current_plan_id ? String(member.current_plan_id) : '';
    const currentPlanAvailable = availablePlans.some((plan) => String(plan.plan_id) === currentPlanId);
    setEditingMemberAvailablePlans(availablePlans);
    setEditingMemberSelectedPlanId(currentPlanAvailable ? currentPlanId : (availablePlans[0]?.plan_id ? String(availablePlans[0].plan_id) : ''));
  };

  const openEditMember = async (member) => {
    const heightUnit = member.height_unit || 'cm';
    const heightParts = splitHeightInches(member.height, heightUnit);
    const nextEditingMember = {
      id: member.id,
      name: member.name,
      email: member.email,
      mobile: member.mobile,
      outlet_id: member.outlet_id,
      outlet_name: member.outlet_name || '',
      weight: member.weight ?? '',
      weight_unit: member.weight_unit || 'kg',
      height: heightUnit === 'ft_in' ? '' : (member.height ?? ''),
      height_unit: heightUnit,
      height_feet: heightParts.feet,
      height_inches: heightParts.inches,
      other_info: member.other_info || '',
      signup_fee_override: member.signup_fee_override ?? '',
      signup_fee_exempt: !!member.signup_fee_exempt,
      member_discount_mode: member.member_discount_mode || 'none',
      member_discount_pct: member.member_discount_pct ?? '',
      current_plan_id: member.current_plan_id || '',
      current_plan_start_date: member.current_plan_start_date || '',
      current_plan_end_date: member.current_plan_end_date || '',
      current_plan_next_due_date: member.current_plan_next_due_date || ''
    };
    setEditingMember(nextEditingMember);
    setEditingMemberStep(1);
    setEditingMemberDocType('kyc');
    setEditingMemberDocuments([]);
    setEditingMemberAvailablePlans([]);
    setEditingMemberSelectedPlanId('');
    setEditingMemberPlanStartDate(toDateInputValue(member.current_plan_end_date || member.current_plan_next_due_date));
    await loadEditingMemberPlanOptions(nextEditingMember);
  };

  const submitEditMemberPlan = async (event) => {
    event.preventDefault();
    if (!editingMember?.id || !editingMemberSelectedPlanId) {
      return;
    }
    const result = await dispatch(
      assignMemberToPlan({
        userId: editingMember.id,
        planId: editingMemberSelectedPlanId,
        startDate: editingMemberPlanStartDate || null,
        partnerId
      })
    );
    if (assignMemberToPlan.fulfilled.match(result)) {
      setEditingMember(null);
      setEditingMemberStep(1);
      setEditingMemberDocType('kyc');
      setEditingMemberDocuments([]);
      setEditingMemberAvailablePlans([]);
      setEditingMemberSelectedPlanId('');
      setEditingMemberPlanStartDate('');
    }
  };
  
  const selectedBillingSubscription = outletSubscriptions?.[String(selectedOutletId)]?.subscription || {};
  const selectedBillingTier = normalizeBillingTier(selectedBillingSubscription?.tier);
  const isStarterBillingTier = selectedBillingTier === 'starter';
  const selectedBillingAlertAccess = getBillingAlertAccess(selectedBillingTier);
  const alertPlanViewEffective = selectedBillingTier || 'growth';
  const alertCenterRows = useMemo(() => filteredAlertQueue.filter((alert) => {
    const source = String(alert?.reminder_source || alert?.alert_type || '').toLowerCase();
    return source.includes('automated') || source.startsWith('automated_') || source === 'overdue_manual';
  }), [filteredAlertQueue]);
  const alertCenterStats = useMemo(() => {
    const rows = alertCenterRows || [];
    const byStatus = rows.reduce((acc, alert) => {
      const statusKey = String(alert?.delivery_status || 'queued').toLowerCase();
      acc[statusKey] = (acc[statusKey] || 0) + 1;
      return acc;
    }, {});
    return {
      total: rows.length,
      queued: byStatus.queued || 0,
      sent: byStatus.sent || 0,
      failed: byStatus.failed || 0,
      passed: byStatus.passed || 0
    };
  }, [alertCenterRows]);
  const billingNextPaymentsColumnCount =
    7 +
    (selectedBillingAlertAccess.hasManualAlerts ? 1 : 0) +
    (selectedBillingAlertAccess.hasAutomatedAlerts ? 1 : 0);
  const billingNextPaymentsTierClass = selectedBillingAlertAccess.hasAutomatedAlerts ? 'automated' : 'no-alerts';

  const getBillingTierForRow = (row) => {
    const outletId = String(row?.outlet_id || selectedOutletId || '');
    return normalizeBillingTier(
      row?.billing_subscription?.tier ||
      outletSubscriptions?.[outletId]?.subscription?.tier ||
      selectedBillingTier
    );
  };

  const getBillingAlertAccessForRow = (row) => getBillingAlertAccess(getBillingTierForRow(row));

  const buildManualAlertMessage = (row, stage = GROWTH_ALERT_STAGES[0]) => {
    const memberName = row?.member_name || 'Member';
    const outletName = row?.outlet_name || 'your gym';
    const planName = row?.plan_name || 'your current plan';
    const planDuration = row?.plan_duration_months
      ? `${row.plan_duration_months} month(s)`
      : null;
    const dueDate = row?.due_date
      ? new Date(row.due_date).toLocaleDateString()
      : 'the scheduled expiry date';

    if (stage?.bucket === 'disable') {
      return `Dear ${memberName}, your "${planName}" membership at ${outletName} remains unpaid after the due date ${dueDate}. Your account may be disabled if payment is not completed immediately. Please renew to continue uninterrupted access.`;
    }
    if (stage?.bucket === 'after') {
      return `Dear ${memberName}, your "${planName}" membership payment at ${outletName} was due on ${dueDate}. Please complete the overdue payment as soon as possible to keep your account active.`;
    }
    return `Dear ${memberName}, we hope you are doing well. This is a reminder from ${outletName} that your current subscription plan, "${planName}"${planDuration ? ` (${planDuration})` : ''}, is due to expire on ${dueDate}. Don't let your fitness momentum slow down. We encourage you to renew before ${dueDate} to continue enjoying uninterrupted access. Thank you for being a valued member.`;
  };

  const getDiscontinueEmailRowKey = (row) => `${row?.user_id || ''}_${row?.outlet_id || ''}`;

  const buildDiscontinueEmailSubject = (row) => {
    const outletName = row?.outlet_name || 'Your gym';
    return `${outletName} subscription cancellation notice`;
  };

  const buildDiscontinueEmailBody = (row) => {
    const memberName = row?.member_name || 'Member';
    const outletName = row?.outlet_name || 'your gym';
    const planName = row?.plan_name || 'your current plan';
    const dueDate = row?.due_date ? new Date(row.due_date).toLocaleDateString() : 'the due date';
    return `Hi ${memberName}, your ${planName} payment for ${outletName} remains unpaid after the due date ${dueDate}. Your subscription is now eligible for cancellation. Please clear the dues immediately if you want to continue.`;
  };

  const openDiscontinueEmailModal = (row) => {
    setDiscontinueEmailModal({ open: true, row, sending: false, error: '' });
  };

  const closeDiscontinueEmailModal = () => {
    setDiscontinueEmailModal({ open: false, row: null, sending: false, error: '' });
  };

  const confirmDiscontinueEmail = async () => {
    if (!discontinueEmailModal.row) {
      return;
    }
    setDiscontinueEmailModal((prev) => ({ ...prev, sending: true, error: '' }));
    const rowKey = getDiscontinueEmailRowKey(discontinueEmailModal.row);
    try {
      await dispatch(triggerOverdueAction({
        memberId: discontinueEmailModal.row.user_id,
        outletId: discontinueEmailModal.row.outlet_id,
        partnerId,
        sendDiscontinueMessage: true,
        disableMember: true,
        sendSms: false,
        sendEmail: true,
      })).unwrap();
      setSentDiscontinueEmailRows((prev) => prev.includes(rowKey) ? prev : [...prev, rowKey]);
      showNotification('success', 'topRight', 'Discontinuation email sent', 'Member has been disabled and the email was sent successfully.');
      closeDiscontinueEmailModal();
    } catch (error) {
      setDiscontinueEmailModal((prev) => ({ ...prev, sending: false, error: String(error) || 'Failed to send discontinuation email.' }));
    }
  };

  const openManualAlertModal = (row) => {
    const alertAccess = getBillingAlertAccessForRow(row);
    if (!alertAccess.hasManualAlerts) {
      const message = alertAccess.tier === 'pro'
        ? 'Pro plan handles payment reminders automatically.'
        : 'Starter plan does not include payment reminder alerts.';
      showNotification('warning', 'topRight', 'Manual alerts unavailable', message);
      return;
    }
    const defaultStage = GROWTH_ALERT_STAGES[0];
    setManualAlertModal({
      open: true,
      row,
      mode: 'email',
      alertType: defaultStage.key,
      scheduledDate: addDaysToDateInput(row?.due_date, defaultStage.offsetDays),
      message: buildManualAlertMessage(row, defaultStage)
    });
  };

  const updateManualAlertStage = (alertType) => {
    const stage = GROWTH_ALERT_STAGES.find((item) => item.key === alertType) || GROWTH_ALERT_STAGES[0];
    setManualAlertModal((prev) => ({
      ...prev,
      alertType: stage.key,
      scheduledDate: addDaysToDateInput(prev?.row?.due_date, stage.offsetDays),
      message: buildManualAlertMessage(prev?.row, stage)
    }));
  };

  const sendManualAlertFromModal = async () => {
    if (!manualAlertModal?.row?.user_id || !manualAlertModal?.row?.outlet_id) {
      return;
    }
    if (!manualAlertModal.mode) {
      return;
    }
    if (!manualAlertModal.alertType || !manualAlertModal.scheduledDate) {
      showNotification('error', 'topRight', 'Schedule required', 'Choose the reminder slot and scheduled date.');
      return;
    }
    if (!getBillingAlertAccessForRow(manualAlertModal.row).hasManualAlerts) {
      showNotification('warning', 'topRight', 'Manual alerts unavailable', 'Manual alerts are available only on the Growth plan.');
      return;
    }
    const result = await dispatch(
      triggerManualAlert({
        memberId: manualAlertModal.row.user_id,
        outletId: manualAlertModal.row.outlet_id,
        partnerId,
        mode: manualAlertModal.mode,
        alertType: manualAlertModal.alertType,
        scheduledFor: toScheduledIso(manualAlertModal.scheduledDate),
        message: manualAlertModal.message
      })
    );
    if (triggerManualAlert.fulfilled.match(result)) {
      setManualAlertModal({ open: false, row: null, mode: 'email', alertType: 'manual_before_due_1', scheduledDate: '', message: '' });
      setActiveMenu('Alert Queue');
    }
  };

  const buildRenewalPaymentRow = () => {
    if (!selectedRenewal || !renewalCheckoutSummary) {
      return null;
    }
    return {
      ...selectedRenewal,
      plan_id: renewalSelectedPlanId || selectedRenewal.plan_id,
      plan_name: renewalCheckoutSummary.newPlanName || selectedRenewal.plan_name,
      base_amount: renewalCheckoutSummary.baseAmount,
      due_amount: renewalCheckoutSummary.finalAmount,
      amount: renewalCheckoutSummary.finalAmount,
      discount_amount: renewalCheckoutSummary.savingsAmount,
      plan_discount_amount: renewalCheckoutSummary.planDiscountAmount,
      plan_discount_pct: renewalCheckoutSummary.planDiscountPct,
      member_discount_amount: renewalCheckoutSummary.memberDiscountAmount,
      member_discount_pct: renewalCheckoutSummary.memberDiscountPct,
      member_discount_mode: renewalCheckoutSummary.memberDiscountMode,
      partner_discount_amount: renewalCheckoutSummary.partnerDiscountAmount,
      partner_discount_pct: renewalCheckoutSummary.partnerDiscountPct,
      signup_fee: renewalCheckoutSummary.signupFee,
      currency: renewalCheckoutSummary.currency,
      renewal_plan_changed: String(renewalSelectedPlanId || '') !== String(selectedRenewal.plan_id || ''),
      plan_start_date: renewalPlanStartDate || todayDateInputValue(),
      plan_end_date: renewalCheckoutSummary.planEndDate || null
    };
  };

  const openMarkPaidModal = (row) => {
    setMarkPaidModal({ open: true, row, transactionId: '' });
  };

  const confirmMarkPaid = async () => {
    if (!markPaidModal?.row?.user_id) {
      return;
    }
    const result = await dispatch(
      confirmMemberUpiPayment({
        memberId: markPaidModal.row.user_id,
        transactionId: markPaidModal.transactionId || null,
        amount: Number(markPaidModal.row?.due_amount ?? markPaidModal.row?.amount ?? 0),
        planId: markPaidModal.row?.plan_id || null,
        startDate: markPaidModal.row?.plan_start_date || null,
        partnerId
      })
    );
    if (confirmMemberUpiPayment.fulfilled.match(result)) {
      setMarkPaidModal({ open: false, row: null, transactionId: '' });
      setBillingTab('PAYMENT_HISTORY');
    }
  };

  const openUpiQrModal = (row) => {
    const amount = Number(row?.due_amount ?? row?.amount ?? 0);
    const upiId = billingSummary?.partner_upi_id || '';
    if (!upiId) {
      showNotification('error', 'topRight', 'UPI not configured', 'Partner UPI ID is not set. Ask superadmin to update onboarding details.');
      return;
    }
    const payeeName = 'iFit Manager';
    const note = `Membership Fee - ${row?.member_name || 'Member'}`;
    const upiUri =
      `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(payeeName)}` +
      `&am=${amount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(note)}`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(upiUri)}`;
    setUpiModal({ open: true, row, upiUri, qrCodeUrl });
  };

  const openDueHistoryModal = (row) => {
    const now = Date.now();
    const threeMonthsAgo = now - 90 * 24 * 60 * 60 * 1000;
    const history = (billingSummary?.payment_history || [])
      .filter((h) => String(h?.user_id) === String(row?.user_id))
      .filter((h) => {
        const ts = h?.date_time ? new Date(h.date_time).getTime() : 0;
        return ts >= threeMonthsAgo;
      })
      .sort((a, b) => new Date(b?.date_time || 0).getTime() - new Date(a?.date_time || 0).getTime())
      .slice(0, 6);
    setDueHistoryModal({ open: true, row, history });
  };

  const openBreakupModal = (row) => {
    setBreakupModal({ open: true, row });
  };

  const openHoldModal = (row) => {
    setHoldModal({
      open: true,
      row,
      holdMonths: 1,
      holdingChargePerMonth: row?.hold_charge_per_month ? String(row.hold_charge_per_month) : ''
    });
  };

  const submitHold = async () => {
    if (!holdModal?.row?.user_id || !holdModal?.row?.outlet_id) {
      return;
    }
    const months = Number(holdModal.holdMonths);
    const chargePerMonth = Number(holdModal.holdingChargePerMonth);
    if (!Number.isFinite(months) || months < 1 || months > 3) {
      showNotification('warning', 'topRight', 'Invalid hold period', 'Hold months must be between 1 and 3.');
      return;
    }
    if (!Number.isFinite(chargePerMonth) || chargePerMonth < 0) {
      showNotification('warning', 'topRight', 'Invalid holding charge', 'Holding charge per month must be 0 or more.');
      return;
    }
    const result = await dispatch(
      startMembershipHold({
        memberId: holdModal.row.user_id,
        outletId: holdModal.row.outlet_id,
        partnerId,
        holdMonths: months,
        holdingChargePerMonth: chargePerMonth
      })
    );
    if (startMembershipHold.fulfilled.match(result)) {
      setHoldModal({ open: false, row: null, holdMonths: 1, holdingChargePerMonth: '' });
      setBillingTab('ON_HOLD');
    }
  };

  const openResumeHoldModal = (row) => {
    setResumeHoldModal({
      open: true,
      row,
      amount: String(row?.hold_fee_paid ?? 0),
      method: 'UPI',
      transactionId: ''
    });
  };

  const submitResumeHold = async () => {
    if (!resumeHoldModal?.row?.user_id || !resumeHoldModal?.row?.outlet_id) {
      return;
    }
    const amount = Number(resumeHoldModal.amount);
    if (!Number.isFinite(amount) || amount < 0) {
      showNotification('warning', 'topRight', 'Invalid amount', 'Please enter a valid holding charge amount.');
      return;
    }
    const result = await dispatch(
      resumeMembershipHold({
        memberId: resumeHoldModal.row.user_id,
        outletId: resumeHoldModal.row.outlet_id,
        partnerId,
        amount,
        transactionId: resumeHoldModal.transactionId || null,
        method: resumeHoldModal.method || 'UPI'
      })
    );
    if (resumeMembershipHold.fulfilled.match(result)) {
      setResumeHoldModal({ open: false, row: null, amount: '', method: 'UPI', transactionId: '' });
      setBillingTab('NEXT_PAYMENTS');
    }
  };

  const toggleOverdueSort = (key) => {
    setOverdueSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'daysOverdue' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key) => {
    if (overdueSort.key !== key) {
      return '';
    }
    return overdueSort.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const pendingPlanActionIsDelete = planActionConfirm?.type === 'delete';
  const pendingPlanActionLabel = pendingPlanActionIsDelete
    ? 'Delete'
    : (planActionConfirm?.plan?.active ? 'Disable' : 'Enable');
  const pendingPlanActionClass = pendingPlanActionIsDelete
    ? 'btn-delete'
    : (planActionConfirm?.plan?.active ? 'btn-disable' : 'btn-enable');
  const partnerInitial = (partnerDisplayName || 'U').trim().charAt(0).toUpperCase();
  const pendingAlertCount = (alertQueue || []).filter((item) => {
    const outletMatches = !selectedOutletId || String(item?.outlet_id || '') === String(selectedOutletId);
    return outletMatches && String(item?.delivery_status || '').toLowerCase() === 'queued';
  }).length;
  const activeOutletCount = outlets.filter((outlet) => outlet.is_active).length;
  const inactiveOutletCount = Math.max(outlets.length - activeOutletCount, 0);
  const planLimitAlertAccess = getBillingAlertAccessForRow(planLimitModal?.row);
  const planLimitTierLabel = getBillingTierLabel(planLimitAlertAccess.tier);
  const partnerUpiConfigured = Boolean(billingSummary?.partner_upi_id);

  useEffect(() => {
    if (activeMenu === 'Alert Queue' && isStarterBillingTier) {
      setActiveMenu('Billing');
    }
  }, [activeMenu, isStarterBillingTier]);

  return (
    <main className="partner-layout">
      <header className="partner-header">
        <div className="partner-header__brand">
          <BrandLogo compact />
          <span className="partner-header__workspace">Partner Workspace</span>
        </div>
        <label className="partner-search">
          <SearchOutlinedIcon fontSize="small" />
          <input
            value={globalSearch}
            onChange={(event) => setGlobalSearch(event.target.value)}
            placeholder="Search outlets, members, plans..."
            aria-label="Search outlets, members, plans"
          />
        </label>
        <div className="header-actions" aria-label="Workspace controls">
          {!isStarterBillingTier ? (
            <button className="header-icon-btn header-icon-btn--notify" type="button" title="Alert queue" aria-label={`Alert queue${pendingAlertCount ? `, ${pendingAlertCount} queued` : ''}`} onClick={() => setActiveMenu('Alert Queue')}>
              <NotificationsNoneOutlinedIcon fontSize="small" />
              {pendingAlertCount > 0 ? <span className="header-icon-btn__badge">{pendingAlertCount}</span> : null}
            </button>
          ) : null}
          <div className="partner-user-card" aria-label={`Signed in as ${partnerDisplayName}`}>
            <span className="partner-user-card__avatar" aria-hidden="true">{partnerInitial}</span>
            <div className="partner-user-card__copy">
              <span>Signed in as</span>
              <strong>{partnerDisplayName}</strong>
            </div>
          </div>
          <div className="outlet-switcher" ref={outletSwitcherRef}>
            <button
              className="outlet-switcher__button"
              type="button"
              aria-haspopup="listbox"
              aria-expanded={outletSwitcherOpen}
              onClick={() => setOutletSwitcherOpen((open) => !open)}
              disabled={outlets.length === 0}
            >
              <span>{selectedOutlet?.name || 'Select outlet'}</span>
              <span className="outlet-switcher__chevron" aria-hidden="true" />
            </button>
            {outletSwitcherOpen ? (
              <div className="outlet-switcher__menu" role="listbox" aria-label="Switch outlet">
                {outlets.map((o) => {
                  const isSelected = String(o.id) === String(selectedOutletId);
                  return (
                    <button
                      key={o.id}
                      className={`outlet-switcher__option${isSelected ? ' active' : ''}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => switchOutlet(String(o.id))}
                    >
                      {o.name}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          <button className="header-icon-btn header-icon-btn--refresh" type="button" onClick={refreshAll} title="Refresh workspace" aria-label="Refresh workspace">
            <RefreshOutlinedIcon fontSize="small" />
          </button>
          <button className="header-icon-btn header-icon-btn--logout" type="button" onClick={() => dispatch(logout())} title="Logout" aria-label="Logout">
            <LogoutOutlinedIcon fontSize="small" />
          </button>
        </div>
      </header>

      <section className={`partner-shell${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <aside className={`partner-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`}>
          <button
            className="sidebar-collapse-btn"
            type="button"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
          >
            {sidebarCollapsed ? <ChevronRightOutlinedIcon fontSize="small" /> : <ChevronLeftOutlinedIcon fontSize="small" />}
          </button>
          <div className="sidebar-nav">
            {MENU_ITEMS.filter((item) => ['Home', 'My Outlets'].includes(item.key)).map((item) => (
              <button
                key={item.key}
                type="button"
                title={item.label}
                className={`side-btn ${activeMenu === item.key ? 'active' : ''}`}
                onClick={() => setActiveMenu(item.key)}
              >
                <span className="side-btn-content">
                  <span className="side-btn-icon"><item.icon fontSize="small" /></span>
                  <span className="side-btn-label">{item.label}</span>
                </span>
              </button>
            ))}
            <p className="sidebar-nav-label">Manage</p>
            {MENU_ITEMS.filter((item) => ['My Members', 'My Plans', 'Plan Renewal', 'Billing'].includes(item.key)).map((item) => (
              <button
                key={item.key}
                type="button"
                title={item.label}
                className={`side-btn ${activeMenu === item.key ? 'active' : ''}`}
                onClick={() => setActiveMenu(item.key)}
              >
                <span className="side-btn-content">
                  <span className="side-btn-icon"><item.icon fontSize="small" /></span>
                  <span className="side-btn-label">{item.label}</span>
                </span>
              </button>
            ))}
            <p className="sidebar-nav-label">Operations</p>
            {MENU_ITEMS.filter((item) => ['Alert Queue', 'Analytics'].includes(item.key))
              .filter((item) => item.key !== 'Alert Queue' || !isStarterBillingTier)
              .map((item) => (
              <button
                key={item.key}
                type="button"
                title={item.label}
                className={`side-btn ${activeMenu === item.key ? 'active' : ''}`}
                onClick={() => setActiveMenu(item.key)}
              >
                <span className="side-btn-content">
                  <span className="side-btn-icon"><item.icon fontSize="small" /></span>
                  <span className="side-btn-label">{item.label}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="sidebar-insight-card">
            <QueryStatsOutlinedIcon fontSize="small" />
            <strong>Grow your business</strong>
            <span>Explore analytics to boost engagement.</span>
            <button className="sidebar-insight-card__link" type="button" onClick={() => setActiveMenu('Analytics')}>
              View Analytics
            </button>
          </div>
        </aside>

        <section className="partner-main">
          {status === 'loading' ? <p className="muted">Loading partner data...</p> : null}

          {activeMenu === 'Home' ? (
            <article className="panel">
              <div className="section-header">
                <div>
                  <h2>Home</h2>
                  <p className="muted">Outlet overview and subscription plans.</p>
                </div>
                <div className="row-actions">
                  <label>Outlet<input value={selectedOutlet?.name || ''} readOnly /></label>
                </div>
              </div>

              <div className="table-wrap">
                <table className="pro-table mobile-cards table-partner-outlet-plans">
                  <thead>
                    <tr>
                      <th>Outlet</th>
                      <th>Billing Tier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outlets.length === 0 ? (
                      <tr><td colSpan="2">No outlets found.</td></tr>
                    ) : (
                      outlets.map((outlet) => {
                        const outletId = String(outlet.id);
                        const tier = outletSubscriptions[outletId]?.subscription?.tier || '-';
                        return (
                          <tr key={outlet.id}>
                            <td>{outlet.name}</td>
                            <td>{tier}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="plan-card-grid">
                {[
                  {
                    key: 'starter',
                    badge: 'Starter',
                    title: 'Starter',
                    price: `${formatMoney('INR', planCharges?.starter ?? 599)} / month`,
                    summary: 'Essential billing and member management',
                    features: [
                      'Billing dashboard for real-time payment tracking',
                      'Member management across outlets',
                      'Payment insights for upcoming and overdue dues',
                      'Analytics for revenue and collection visibility',
                      'Member stats dashboard for daily operations'
                    ],
                    note: 'No payment reminder alerts are included in Starter.'
                  },
                  {
                    key: 'growth',
                    badge: 'Growth',
                    title: 'Growth',
                    price: `${formatMoney('INR', planCharges?.growth ?? 1499)} / month`,
                    summary: 'Everything in Starter, plus automated email reminders',
                    baseFeatures: [
                      'Everything in Starter, plus:'
                    ],
                    features: [
                      '6 automated email reminders per member cycle',
                      'Fixed schedule: 5, 3, and 1 days before the due date',
                      '1 automated email on the due date',
                      'Automated emails 1 and 2 days after the due date',
                      'Partner can send one discontinuation email'
                    ],
                    note: 'Reminders are sent automatically by email on the fixed schedule.',
                    highlight: true
                  }
                ].map((card) => {
                  const currentTier = selectedBillingTier;
                  const isCurrent = currentTier && currentTier === card.key;
                  const tierClass = `plan-card--${card.key}`;
                  return (
                    <article key={card.key} className={`plan-card ${tierClass} ${isCurrent ? 'active' : 'disabled'} ${card.highlight ? 'plan-card--featured' : ''}`}>
                      {card.badge && <span className="plan-badge">{card.badge}</span>}
                      <h3>{card.title}</h3>
                      <p className="plan-price">{card.price}</p>
                      <p className="muted">{card.summary}</p>
                      {card.baseFeatures && (
                        <ul className="plan-feature-list">
                          {card.baseFeatures.map((feature) => (
                            <li key={feature} className="plan-base-feature">{feature}</li>
                          ))}
                        </ul>
                      )}
                      <ul className="plan-feature-list">
                        {card.features.map((feature) => (
                          <li key={feature}>{feature}</li>
                        ))}
                      </ul>
                      {card.note && <p className="plan-note">{card.note}</p>}
                      <div className="row-actions">
                        {isCurrent ? (
                          <button className="btn-link btn-cancel mini" type="button" disabled>
                            <CheckCircleOutlineOutlinedIcon fontSize="small" />
                            Current Plan
                          </button>
                        ) : (
                          <button
                            className="btn-link btn-save mini"
                            type="button"
                            disabled={!selectedOutletId}
                            onClick={() =>
                              dispatch(
                                requestPlanUpgrade({
                                  outletId: selectedOutletId,
                                  targetTier: card.key,
                                  partnerId
                                })
                              )
                            }
                          >
                            <CheckCircleOutlineOutlinedIcon fontSize="small" />
                            Switch to {card.title}
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </article>
          ) : null}

          {activeMenu === 'My Outlets' ? (
            <article className="panel workspace-page">
              <div className="workspace-page-header">
                <div>
                  <h2>My Outlets</h2>
                  <p>View and manage all your outlets in one place.</p>
                </div>
              </div>

              <section className="workspace-stats-grid" aria-label="Outlet summary">
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__icon"><StorefrontOutlinedIcon fontSize="small" /></span>
                  <strong>{outlets.length}</strong>
                  <span>Total Outlets</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__dot is-active" />
                  <strong>{activeOutletCount}</strong>
                  <span>Active Outlets</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__dot is-inactive" />
                  <strong>{inactiveOutletCount}</strong>
                  <span>Inactive Outlets</span>
                </article>
                <article className="workspace-stat-card">
                  <span className="workspace-stat-card__icon is-purple"><GroupsOutlinedIcon fontSize="small" /></span>
                  <strong>{members.length || '--'}</strong>
                  <span>Total Members</span>
                </article>
              </section>

              <div className="section-filter-row" role="group" aria-label="Outlet filters">
                <select
                  className="outlet-filter"
                  value={outletFilterStatus}
                  onChange={(event) => setOutletFilterStatus(event.target.value)}
                >
                  <option value="ALL">All</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
                <select
                  className="outlet-filter"
                  value={outletFilterCity}
                  onChange={(event) => setOutletFilterCity(event.target.value)}
                >
                  <option value="ALL">By City</option>
                  {outletCities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              <div className="table-wrap workspace-table-card">
                <table className="pro-table mobile-cards table-partner-outlets">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Address</th>
                      <th>Onboarded On</th>
                      <th>No. of Members</th>
                      <th>Signup Fee</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOutlets.length === 0 ? (
                      <tr><td colSpan="6">No outlets found.</td></tr>
                    ) : (
                      filteredOutlets.map((o) => {
                        const memberCount = memberCountsByOutletId[String(o.id)] || 0;
                        const addressParts = [o.country, o.city, o.state, o.address, o.pincode].filter(Boolean);
                        const formattedAddress = addressParts.join(', ');

                        return (
                          <tr key={o.id}>
                            <td>
                              <div className="outlet-name-cell">
                                <span className="outlet-name-cell__icon"><StorefrontOutlinedIcon fontSize="small" /></span>
                                <span>
                                  <strong>{o.name}</strong>
                                  <small>ID: {String(o.pincode || o.id || '-').slice(0, 8)}</small>
                                </span>
                              </div>
                            </td>
                            <td>{formattedAddress || '-'}</td>
                            <td>{o.created_on ? new Date(o.created_on).toLocaleDateString() : '-'}</td>
                            <td>{memberCount}</td>
                            <td>
                              <div className="row-actions">
                                <input
                                  className="outlet-filter"
                                  type="number"
                                  min="0"
                                  value={outletSignupFeeDrafts[String(o.id)] ?? (o.signup_fee_default ?? 0)}
                                  onChange={(e) =>
                                    setOutletSignupFeeDrafts((prev) => ({
                                      ...prev,
                                      [String(o.id)]: e.target.value
                                    }))
                                  }
                                />
                                <button
                                  className="btn-link btn-save mini"
                                  type="button"
                                  disabled={actionStatus === 'loading'}
                                  onClick={() =>
                                    dispatch(
                                      updateOutletSignupFee({
                                        outletId: String(o.id),
                                        signupFeeDefault: Number(outletSignupFeeDrafts[String(o.id)] ?? (o.signup_fee_default ?? 0))
                                      })
                                    )
                                  }
                                >
                                  <SaveOutlinedIcon fontSize="small" />
                                  Save
                                </button>
                              </div>
                            </td>
                            <td><StatusIcon active={o.is_active} /></td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                <div className="workspace-table-footer">
                  <span>Showing {filteredOutlets.length} of {outlets.length} outlets</span>
                  <span className="workspace-table-footer__pager">1</span>
                </div>
              </div>
            </article>
          ) : null}

          {activeMenu === 'My Plans' ? (
            <>
              <article className="panel">
                <div className="section-header">
                  <ContextTitle label="My Membership Plans" name={selectedOutletLabel} />
                  <div className="section-header-actions">
                    <button className="btn-link btn-add" type="button" onClick={() => setPlanModalType('create')}>
                      <AddCircleOutlineOutlinedIcon fontSize="small" />
                      Add Plan
                    </button>
                  </div>
                </div>
                <div className="table-wrap">
                  <table className="pro-table mobile-cards table-partner-plans">
                    <thead>
                      <tr>
                        <th>Plan Name</th><th>Description</th><th>Charge</th><th>Discount</th><th>Duration</th><th>Status</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlans.length === 0 ? <tr><td colSpan="7">No plans.</td></tr> : filteredPlans.map((p) => (
                        <tr key={p.id}>
                          <td>{p.plan?.name || p.plan?.description || '-'}</td>
                          <td>{p.plan?.description || '-'}</td>
                          <td>{p.plan?.currency || 'INR'} {p.plan?.charge}</td>
                          <td>
                            {(Number(p.plan?.discount_pct || 0) > 0 || Number(p.plan?.discount_amount || 0) > 0)
                              ? `${formatCalculatedInput(p.plan?.discount_pct || 0)}% (${p.plan?.currency || 'INR'} ${formatCalculatedInput(p.plan?.discount_amount || 0)})`
                              : '-'}
                          </td>
                          <td>{p.plan?.duration_months}</td>
                          <td><StatusIcon active={p.active} /></td>
                          <td>
                            <div className="row-actions">
                              <button className="btn-link btn-edit mini" type="button" onClick={() => {
                                setEditingPlan({
                                  id: p.id,
                                  name: p.plan?.name ?? '',
                                  charge: p.plan?.charge ?? '',
                                  duration_months: p.plan?.duration_months ?? '',
                                  description: p.plan?.description ?? '',
                                  discount_pct: p.plan?.discount_pct ?? '',
                                  discount_amount: p.plan?.discount_amount ?? '',
                                  trainer_support: !!p.plan?.trainer_support,
                                  currency: p.plan?.currency ?? 'INR'
                                });
                                setEditingPlanDiscountSource(Number(p.plan?.discount_amount || 0) > 0 ? 'amount' : 'percent');
                                setPlanModalType('edit');
                              }}><EditOutlinedIcon fontSize="small" />Edit</button>
                              <button className={`btn-link ${p.active ? 'btn-disable' : 'btn-enable'} mini`} type="button" onClick={() => setPlanActionConfirm({ type: 'toggle', plan: p })}>
                                {p.active ? <BlockOutlinedIcon fontSize="small" /> : <CheckCircleOutlineOutlinedIcon fontSize="small" />}
                                {p.active ? 'Disable' : 'Enable'}
                              </button>
                              <button className="btn-link btn-delete mini" type="button" data-click-guard="off" onClick={() => setPlanActionConfirm({ type: 'delete', plan: p })}><DeleteOutlineOutlinedIcon fontSize="small" />Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            </>
          ) : null}

          {activeMenu === 'My Members' ? (
            <>
              <article className="panel">
                <div className="section-header">
                  <ContextTitle label="Members" relation="of" name={selectedOutletLabel} />
                  <div className="section-header-actions">
                    <button className="btn-link btn-add" type="button" onClick={startMemberOnboarding}><AddCircleOutlineOutlinedIcon fontSize="small" />Add Member</button>
                  </div>
                </div>
                <div className="section-filter-row">
                  <label>Outlet<input value={selectedOutlet?.name || ''} readOnly /></label>
                  <select className="outlet-filter" value={memberFilterStatus} onChange={(event) => setMemberFilterStatus(event.target.value)}>
                    <option value="ALL">All</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                  <select className="outlet-filter" value={memberSortOrder} onChange={(event) => setMemberSortOrder(event.target.value)}>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                </div>
                <div className="table-wrap">
                  <table className="pro-table mobile-cards table-partner-members">
                    <thead>
                      <tr><th>Profile</th><th>Member</th><th>Onboarding Date</th><th>Contact Verified</th><th>Identity Verified</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {filteredMembers.length === 0 ? <tr><td colSpan="7">No members found.</td></tr> : filteredMembers.map((m) => (
                        <tr key={`${m.id}-${m.outlet_id}`}>
                          <td>
                            {m.profile_pic_url ? (
                              <img src={m.profile_pic_url} alt={`${m.name || 'Member'} profile`} className="member-avatar" />
                            ) : (
                              <div className="member-avatar member-avatar-fallback">
                                {(m.name || 'M').trim().charAt(0).toUpperCase()}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="member-contact-cell">
                              <strong>{m.name || '-'}</strong>
                              <small>{m.email || '-'}</small>
                              <small>{m.mobile || '-'}</small>
                            </div>
                          </td>
                          <td>{(m.created_on || m.created_at || m.onboarding_date || m.onboarded_on) ? new Date(m.created_on || m.created_at || m.onboarding_date || m.onboarded_on).toLocaleDateString() : '-'}</td>
                          <td>
                            <div className="verification-stack">
                              <VerificationPill verified={!!m.is_email_verified} label="Email" />
                              <VerificationPill verified={!!m.is_mobile_verified} label="Mobile" />
                            </div>
                          </td>
                          <td>
                            <VerificationPill verified={isMemberIdentityVerified(m)} label="Identity" />
                          </td>
                          <td><StatusIcon active={m.is_active} /></td>
                          <td>
                            <div className="row-actions member-table-actions">
                              <button
                                className="btn-link btn-edit mini"
                                type="button"
                                onClick={() => openEditMember(m)}
                              >
                                <EditOutlinedIcon fontSize="small" />
                                Edit
                              </button>
                              <button className={`btn-link ${m.is_active ? 'btn-disable' : 'btn-enable'} mini`} type="button" onClick={() => dispatch(toggleMember({ memberId: m.id, activate: !m.is_active, partnerId }))}>
                                {m.is_active ? <BlockOutlinedIcon fontSize="small" /> : <CheckCircleOutlineOutlinedIcon fontSize="small" />}
                                {m.is_active ? 'Disable' : 'Enable'}
                              </button>
                              <button className="btn-link btn-delete mini" type="button" data-click-guard="off" onClick={() => setMemberDeleteConfirm(m)}><DeleteOutlineOutlinedIcon fontSize="small" />Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>

            </>
          ) : null}

          {activeMenu === 'Billing' ? (
            <article className="panel">
              <ContextTitle label="Billing" name={selectedOutletLabel} standalone />
              <section className="billing-overview" aria-label="Billing summary">
                <button
                  type="button"
                  className={`billing-stat billing-stat--upcoming ${billingTab === 'NEXT_PAYMENTS' ? 'is-active' : ''}`}
                  onClick={() => setBillingTab('NEXT_PAYMENTS')}
                >
                  <span className="billing-stat__icon"><ScheduleOutlinedIcon fontSize="small" /></span>
                  <span className="billing-stat__body">
                    <span className="billing-stat__label">Upcoming</span>
                    <strong className="billing-stat__value">{billingOverview.upcomingCount}</strong>
                    <small className="billing-stat__meta">{formatMoney(billingOverview.currency, billingOverview.upcomingAmount)} due</small>
                  </span>
                </button>
                <button
                  type="button"
                  className={`billing-stat billing-stat--overdue ${billingTab === 'OVERDUE' ? 'is-active' : ''}`}
                  onClick={() => setBillingTab('OVERDUE')}
                >
                  <span className="billing-stat__icon"><WarningAmberOutlinedIcon fontSize="small" /></span>
                  <span className="billing-stat__body">
                    <span className="billing-stat__label">Overdue</span>
                    <strong className="billing-stat__value">{billingOverview.overdueCount}</strong>
                    <small className="billing-stat__meta">{formatMoney(billingOverview.currency, billingOverview.overdueAmount)} pending</small>
                  </span>
                </button>
                <button
                  type="button"
                  className={`billing-stat billing-stat--hold ${billingTab === 'ON_HOLD' ? 'is-active' : ''}`}
                  onClick={() => setBillingTab('ON_HOLD')}
                >
                  <span className="billing-stat__icon"><PauseCircleOutlinedIcon fontSize="small" /></span>
                  <span className="billing-stat__body">
                    <span className="billing-stat__label">On Hold</span>
                    <strong className="billing-stat__value">{billingOverview.onHoldCount}</strong>
                    <small className="billing-stat__meta">members paused</small>
                  </span>
                </button>
                <button
                  type="button"
                  className={`billing-stat billing-stat--collected ${billingTab === 'PAYMENT_HISTORY' ? 'is-active' : ''}`}
                  onClick={() => setBillingTab('PAYMENT_HISTORY')}
                >
                  <span className="billing-stat__icon"><PaidOutlinedIcon fontSize="small" /></span>
                  <span className="billing-stat__body">
                    <span className="billing-stat__label">Collected (This Month)</span>
                    <strong className="billing-stat__value">{formatMoney(billingOverview.currency, billingOverview.collectedAmount)}</strong>
                    <small className="billing-stat__meta">{billingOverview.collectedCount} payments</small>
                  </span>
                </button>
              </section>
            </article>
          ) : null}

          {activeMenu === 'Plan Renewal' ? (
            <article className="panel">
              <ContextTitle label="Plan Renewal" name={selectedOutletLabel} standalone />
              <section className="renewal-experience" aria-label="Membership renewal center">
                <div className="renewal-experience__main">
                  <div className="renewal-kicker">
                    <BoltOutlinedIcon fontSize="small" />
                    Fast membership renewal
                  </div>
                  <ol className="renewal-steps" aria-label="Renewal steps">
                    <li className="is-complete"><span>1</span>Choose member</li>
                    <li className={renewalSelectedPlanId ? 'is-complete' : ''}><span>2</span>Confirm plan</li>
                    <li><span>3</span>Collect payment</li>
                  </ol>
                  {selectedRenewal && renewalCheckoutSummary ? (
                    <>
                      <div className="renewal-hero">
                        <div>
                          <p className="renewal-member">{selectedRenewal.member_name || 'Member'}</p>
                          <h3>{renewalCheckoutSummary.recommendation}</h3>
                          <p className="renewal-subcopy">
                            {selectedRenewal.plan_name || 'Current plan'} at {selectedRenewal.outlet_name || selectedOutletLabel || 'selected outlet'}.
                          </p>
                        </div>
                        <span className={`renewal-status renewal-status--${selectedRenewal.renewalStatus}`}>
                          {renewalCheckoutSummary.currentStatus}
                        </span>
                      </div>

                      <div className="renewal-snapshot-grid">
                        <div className="renewal-snapshot renewal-snapshot--current">
                          <span>Current plan</span>
                          <strong>
                            {selectedRenewalPreviousPurchase
                              ? formatMoney(renewalCheckoutSummary.currency, renewalCheckoutSummary.previousAmount)
                              : 'No payment yet'}
                          </strong>
                          <small>
                            {selectedRenewalPreviousPurchase
                              ? `${selectedRenewalPreviousPurchaseDetails?.planName || selectedRenewal.plan_name || 'Plan'} · ${formatShortDate(selectedRenewalPreviousPurchase.date_time)}`
                              : 'First renewal cycle'}
                          </small>
                          {selectedRenewalPreviousPurchaseDetails ? (
                            <div className="renewal-breakup-summary">
                              <div>Base: {formatMoney(renewalCheckoutSummary.currency, selectedRenewalPreviousPurchaseDetails.baseAmount)}</div>
                              <div>Plan discount: -{formatMoney(renewalCheckoutSummary.currency, selectedRenewalPreviousPurchaseDetails.planDiscountAmount)}</div>
                              <div>Member discount: -{formatMoney(renewalCheckoutSummary.currency, selectedRenewalPreviousPurchaseDetails.memberDiscountAmount)}</div>
                              <div>Signup fee: {formatMoney(renewalCheckoutSummary.currency, selectedRenewalPreviousPurchaseDetails.signupFee)}</div>
                            </div>
                          ) : null}
                        </div>
                        <div className="renewal-snapshot renewal-snapshot--new">
                          <span>2. Choose renewal plan</span>
                          <select
                            className="outlet-filter"
                            value={renewalSelectedPlanId}
                            onChange={(event) => setRenewalSelectedPlanId(event.target.value)}
                          >
                            {renewalPlanOptions.map((planOption) => {
                              const planId = String(planOption?.plan_id || planOption?.plan?.id || '');
                              const planName = planOption?.plan?.name || planOption?.plan_name || planOption?.name || 'Plan';
                              const planPrice = Number(planOption?.plan?.charge ?? planOption?.charge ?? 0) || 0;
                              const planCurrency = planOption?.plan?.currency || planOption?.currency || renewalCheckoutSummary.currency || 'INR';
                              return (
                                <option key={planId} value={planId}>
                                  {planName} · {formatMoney(planCurrency, planPrice)}
                                </option>
                              );
                            })}
                          </select>
                          {renewalSelectedPlanId ? (
                            <>
                              <label className="renewal-start-date">
                                Plan Start Date
                                <input
                                  type="date"
                                  value={renewalPlanStartDate}
                                  onChange={(event) => setRenewalPlanStartDate(event.target.value)}
                                  aria-label="Renewal plan start date"
                                />
                              </label>
                              <small className="renewal-end-date">
                                Plan End Date: {renewalCheckoutSummary.planEndDate ? formatShortDate(renewalCheckoutSummary.planEndDate) : '-'}
                              </small>
                              <div className="renewal-partner-discount">
                                <span className="renewal-partner-discount__label">Partner discount (optional)</span>
                                <div className="renewal-partner-discount__fields">
                                  <label>
                                    Discount %
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                      value={renewalPartnerDiscountPct}
                                      onChange={(event) => updateRenewalPartnerDiscountPercent(event.target.value)}
                                      placeholder="0"
                                      aria-label="Partner discount percent"
                                    />
                                  </label>
                                  <label>
                                    Discount Amount
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={renewalPartnerDiscountAmount}
                                      onChange={(event) => updateRenewalPartnerDiscountAmount(event.target.value)}
                                      placeholder="0"
                                      aria-label="Partner discount amount"
                                    />
                                  </label>
                                </div>
                                <small>Applied after plan and member discounts</small>
                              </div>
                            </>
                          ) : null}
                          <small>{renewalCheckoutSummary.durationMonths} month renewal</small>
                        </div>
                        <div className="renewal-snapshot renewal-snapshot--payment">
                          <span>New payment</span>
                          <strong className={renewalCheckoutSummary.priceDifference > 0 ? 'amount-up' : 'amount-steady'}>{formatMoney(renewalCheckoutSummary.currency, renewalCheckoutSummary.finalAmount)}</strong>
                          <small>{renewalCheckoutSummary.priceDifference == null ? 'New charge' : `${renewalCheckoutSummary.priceDifference > 0 ? '+' : ''}${formatMoney(renewalCheckoutSummary.currency, renewalCheckoutSummary.priceDifference)} from last payment`}</small>
                        </div>
                      </div>

                      <div className="renewal-checkout">
                        <div className="renewal-line">
                          <span>Plan price</span>
                          <strong>{formatMoney(renewalCheckoutSummary.currency, renewalCheckoutSummary.baseAmount)}</strong>
                        </div>
                        <div className="renewal-line renewal-line--saving">
                          <span>
                            <SavingsOutlinedIcon fontSize="small" />
                            Plan & member savings
                          </span>
                          <strong>-{formatMoney(renewalCheckoutSummary.currency, renewalCheckoutSummary.standardSavingsAmount)}</strong>
                        </div>
                        {renewalCheckoutSummary.partnerDiscountAmount > 0 ? (
                          <div className="renewal-line renewal-line--saving">
                            <span>Partner discount</span>
                            <strong>-{formatMoney(renewalCheckoutSummary.currency, renewalCheckoutSummary.partnerDiscountAmount)}</strong>
                          </div>
                        ) : null}
                        <div className="renewal-line renewal-line--final">
                          <span>Final amount</span>
                          <strong>{formatMoney(renewalCheckoutSummary.currency, renewalCheckoutSummary.finalAmount)}</strong>
                        </div>
                      </div>

                      <div className="renewal-actions">
                        <button className="renewal-primary-action" type="button" onClick={() => openMarkPaidModal(buildRenewalPaymentRow())}>
                          <BoltOutlinedIcon fontSize="small" />
                          Confirm Renewal & Collect Payment
                        </button>
                        {partnerUpiConfigured ? (
                          <button className="renewal-secondary-action" type="button" onClick={() => openUpiQrModal(buildRenewalPaymentRow())}>
                            <QrCode2OutlinedIcon fontSize="small" />
                            UPI Checkout
                          </button>
                        ) : null}
                        <button className="renewal-secondary-action" type="button" onClick={() => openBreakupModal(buildRenewalPaymentRow())}>
                          <TrendingUpOutlinedIcon fontSize="small" />
                          Price Details
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="renewal-empty">
                      <CheckCircleOutlineOutlinedIcon fontSize="large" />
                      <h3>No renewal action needed</h3>
                      <p>There are no upcoming or overdue renewals in the selected outlet and filters.</p>
                    </div>
                  )}
                </div>

                <aside className="renewal-queue" aria-label="Renewal recommendations">
                  <div className="renewal-queue__header">
                    <div className="renewal-queue__header-main">
                      <span>1. Choose a member to renew</span>
                      <strong>{renewalCandidates.length}</strong>
                    </div>
                    <label className="renewal-queue-search">
                      <SearchOutlinedIcon fontSize="small" />
                      <input
                        type="search"
                        value={renewalMemberSearch}
                        onChange={(event) => setRenewalMemberSearch(event.target.value)}
                        placeholder="Search member..."
                        aria-label="Search member"
                      />
                    </label>
                  </div>
                  <div className="renewal-queue__list">
                    {renewalCandidates.map((row) => {
                      const rowId = getRenewalRowId(row);
                      const isSelected = rowId === getRenewalRowId(selectedRenewal);
                      const rowAmount = Number(row?.due_amount ?? row?.amount ?? 0) || 0;
                      const rowDays = row.daysUntilDue;
                      return (
                        <button
                          type="button"
                          key={rowId}
                          className={`renewal-queue-item ${isSelected ? 'active' : ''}`}
                          onClick={() => setSelectedRenewalId(rowId)}
                        >
                          <span className={`renewal-dot renewal-dot--${row.renewalStatus}`} />
                          <span className="renewal-queue-item__body">
                            <strong>{row.member_name || 'Member'}</strong>
                            <small>{row.plan_name || 'Plan'} · {formatMoney(row.currency || 'INR', rowAmount)}</small>
                          </span>
                          <span className="renewal-queue-item__date">
                            <CalendarMonthOutlinedIcon fontSize="small" />
                            {rowDays == null ? '-' : (rowDays < 0 ? `${Math.abs(rowDays)}d late` : `${rowDays}d`)}
                          </span>
                        </button>
                      );
                    })}
                    {renewalCandidates.length === 0 ? (
                      <div className="renewal-queue-empty">All clear</div>
                    ) : null}
                  </div>
                </aside>
              </section>
            </article>
          ) : null}

          {activeMenu === 'Billing' ? (
            <article className="panel">
              <div className="billing-table-shell">
                <div className="billing-tabs" role="tablist" aria-label="Billing views">
                  <button type="button" className={`billing-tab ${billingTab === 'NEXT_PAYMENTS' ? 'active' : ''}`} onClick={() => setBillingTab('NEXT_PAYMENTS')}>Next Payments<span className="billing-tab__count">{billingOverview.upcomingCount}</span></button>
                  <button type="button" className={`billing-tab ${billingTab === 'OVERDUE' ? 'active' : ''}`} onClick={() => setBillingTab('OVERDUE')}>Overdue<span className="billing-tab__count billing-tab__count--danger">{billingOverview.overdueCount}</span></button>
                  <button type="button" className={`billing-tab ${billingTab === 'ON_HOLD' ? 'active' : ''}`} onClick={() => setBillingTab('ON_HOLD')}>On Hold<span className="billing-tab__count">{billingOverview.onHoldCount}</span></button>
                  <button type="button" className={`billing-tab ${billingTab === 'PAYMENT_HISTORY' ? 'active' : ''}`} onClick={() => setBillingTab('PAYMENT_HISTORY')}>Payment History</button>
                </div>

                <div className="billing-filters">
                  <label>
                    Outlet
                    <input value={selectedOutlet?.name || ''} readOnly />
                  </label>
                  <label>
                    Member Name
                    <input
                      type="text"
                      placeholder="Search member..."
                      value={billingMemberFilter}
                      onChange={(e) => setBillingMemberFilter(e.target.value)}
                    />
                  </label>
                  {billingTab === 'NEXT_PAYMENTS' ? (
                    <label>
                      Due Date
                      <select className="outlet-filter" value={billingDueFilter} onChange={(e) => setBillingDueFilter(e.target.value)}>
                        <option value="ALL">All</option>
                        <option value="NEXT_5_DAYS">Next 5 Days</option>
                        <option value="NEXT_10_DAYS">Next 10 Days</option>
                        <option value="NEXT_15_DAYS">Next 15 Days</option>
                      </select>
                    </label>
                  ) : null}
                </div>

                <div className="billing-table-frame">
              {billingTab === 'NEXT_PAYMENTS' ? (
                <>
                  <div className="table-wrap">
                    <table className={`pro-table mobile-cards table-billing-next table-billing-next--${billingNextPaymentsTierClass}`}>
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Mobile</th>
                          <th>Outlet</th>
                          <th>Plan</th>
                          <th>Due Date</th>
                          <th>Due Amount</th>
                          
                        
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredNextPayments.length === 0 ? (
                          <tr><td colSpan={billingNextPaymentsColumnCount}>No upcoming payments in selected filter.</td></tr>
                        ) : (
                          filteredNextPayments.map((row) => {
                            const rowAlertAccess = getBillingAlertAccessForRow(row);
                            return (
                              <tr key={`${row.user_id}-${row.plan_id}-${row.due_date}`}>
                                <td>{row.member_name}</td>
                                <td>{row.mobile || '-'}</td>
                                <td>{row.outlet_name || '-'}</td>
                                <td>{row.plan_name || '-'}</td>
                                <td>
                                  {row.due_date ? new Date(row.due_date).toLocaleDateString() : '-'}
                                  <button className="info-icon-btn" type="button" title="View due history" onClick={() => openDueHistoryModal(row)}>i</button>
                                </td>
                                <td>
                                  {row.currency || 'INR'} {row.due_amount ?? row.amount}
                                  <button className="info-icon-btn" type="button" title="View price breakup" onClick={() => openBreakupModal(row)}>i</button>
                                </td>
                                {selectedBillingAlertAccess.hasManualAlerts ? (
                                  <td>
                                    {(row?.member_alert_usage?.manual_alert ?? 0)} / {(row?.member_alert_allowed?.manual_alert ?? 0)}
                                    <button className="info-icon-btn" type="button" title="View this member's plan limits" onClick={() => setPlanLimitModal({ open: true, row })}>i</button>
                                  </td>
                                ) : null}
                               
                                <td>
                                  <div className="row-actions">
                                    <button className="btn-link btn-paid mini" type="button" onClick={() => openMarkPaidModal(row)}>
                                      <PaidOutlinedIcon fontSize="small" />
                                      Mark as Paid
                                    </button>
                                    {partnerUpiConfigured ? (
                                      <button className="btn-link btn-upi mini" type="button" onClick={() => openUpiQrModal(row)}>
                                        <QrCode2OutlinedIcon fontSize="small" />
                                        Pay by UPI
                                      </button>
                                    ) : null}
                                    <button className="btn-link btn-disable mini" type="button" onClick={() => openHoldModal(row)}>
                                      <BlockOutlinedIcon fontSize="small" />
                                      Put on Hold
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}

              {billingTab === 'OVERDUE' ? (
                <>
                  <div className="table-wrap">
                    <table className="pro-table mobile-cards table-billing-overdue">
                      <thead>
                        <tr>
                          <th className="th-sortable" onClick={() => toggleOverdueSort('member_name')}>Member{sortIndicator('member_name')}</th>
                          <th>Mobile</th>
                          <th className="th-sortable" onClick={() => toggleOverdueSort('outlet_name')}>Outlet{sortIndicator('outlet_name')}</th>
                          <th>Plan</th>
                          <th className="th-sortable" onClick={() => toggleOverdueSort('amount')}>Amount{sortIndicator('amount')}</th>
                          <th className="th-sortable" onClick={() => toggleOverdueSort('due_date')}>Due Date{sortIndicator('due_date')}</th>
                          <th className="th-sortable" onClick={() => toggleOverdueSort('daysOverdue')}>Days Overdue{sortIndicator('daysOverdue')}</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOverduePayments.length === 0 ? (
                          <tr><td colSpan="8">No overdue payments in selected filter.</td></tr>
                        ) : (
                          filteredOverduePayments.map((row) => {
                            const rowAlertAccess = getBillingAlertAccessForRow(row);
                            return (
                              <tr key={`${row.user_id}-${row.plan_id}-${row.due_date}`} className="billing-row--overdue">
                                <td>{row.member_name}</td>
                                <td>{row.mobile || '-'}</td>
                                <td>{row.outlet_name || '-'}</td>
                                <td>{row.plan_name || '-'}</td>
                                <td>{row.currency || 'INR'} {row.amount}</td>
                                <td>{row.due_date ? new Date(row.due_date).toLocaleDateString() : '-'}</td>
                                <td className="billing-days-overdue">{row.daysOverdue}</td>
                                <td>
                                  <div className="row-actions">
                                    <button className="btn-link btn-paid mini" type="button" onClick={() => openMarkPaidModal(row)}>
                                      <PaidOutlinedIcon fontSize="small" />
                                      Collect Now
                                    </button>
                                    <button className="btn-link btn-disable mini" type="button" onClick={() => openHoldModal(row)}>
                                      <BlockOutlinedIcon fontSize="small" />
                                      Put on Hold
                                    </button>
                                    {rowAlertAccess.hasDiscontinueEmail ? (
                                      <button
                                        className="btn-link btn-alert mini"
                                        type="button"
                                        disabled={
                                          row.member_status !== 'ACTIVE' ||
                                          sentDiscontinueEmailRows.includes(getDiscontinueEmailRowKey(row)) ||
                                          actionStatus === 'loading'
                                        }
                                        onClick={() => openDiscontinueEmailModal(row)}
                                      >
                                        <NotificationsActiveOutlinedIcon fontSize="small" />
                                        {row.member_status !== 'ACTIVE' || sentDiscontinueEmailRows.includes(getDiscontinueEmailRowKey(row))
                                          ? 'Email Sent'
                                          : 'Send Discontinuation Email'}
                                      </button>
                                    ) : null}
                                    <button
                                      className="btn-link btn-disable mini"
                                      type="button"
                                      onClick={() =>
                                        dispatch(
                                          triggerOverdueAction({
                                            memberId: row.user_id,
                                            outletId: row.outlet_id,
                                            partnerId,
                                            sendDiscontinueMessage: false,
                                            disableMember: true,
                                            sendSms: false,
                                            sendEmail: false
                                          })
                                        )
                                      }
                                    >
                                      <BlockOutlinedIcon fontSize="small" />
                                      Disable User
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}

              {billingTab === 'ON_HOLD' ? (
                <>
                  <div className="table-wrap">
                    <table className="pro-table mobile-cards table-billing-on-hold">
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Outlet</th>
                          <th>Plan</th>
                          <th>Status</th>
                          <th>Hold Months</th>
                          <th>Charge / Month</th>
                          <th>Total Hold Charge</th>
                          <th>Hold Expires</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOnHoldMembers.length === 0 ? (
                          <tr><td colSpan="9">No members are currently on hold.</td></tr>
                        ) : (
                          filteredOnHoldMembers.map((row) => (
                            <tr key={`${row.user_id}-${row.plan_id}-${row.hold_expiry_date || row.due_date || ''}`}>
                              <td>{row.member_name}</td>
                              <td>{row.outlet_name || '-'}</td>
                              <td>{row.plan_name || '-'}</td>
                              <td>{row.member_status || 'HOLD'}</td>
                              <td>{row.hold_months ?? 0}</td>
                              <td>INR {row.hold_charge_per_month ?? 0}</td>
                              <td>INR {row.hold_fee_paid ?? 0}</td>
                              <td>
                                {row.hold_expiry_date ? new Date(row.hold_expiry_date).toLocaleDateString() : '-'}
                                {row.hold_flag ? ` (${row.hold_flag})` : ''}
                              </td>
                              <td>
                                <div className="row-actions">
                                  <button className="btn-link btn-paid mini" type="button" onClick={() => openResumeHoldModal(row)}>
                                    <PaidOutlinedIcon fontSize="small" />
                                    Mark Paid & Resume
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}

              {billingTab === 'PAYMENT_HISTORY' ? (
                <>
                  <div className="table-wrap">
                    <table className="pro-table mobile-cards table-billing-history">
                      <thead>
                        <tr>
                          <th>Member</th><th>Mobile</th><th>Outlet</th><th>Amount</th><th>Method</th><th>Transaction</th><th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPaymentHistory.length === 0 ? (
                          <tr><td colSpan="7">No payment history in selected filter.</td></tr>
                        ) : (
                          filteredPaymentHistory.map((row) => (
                            <tr key={row.id}>
                              <td>{row.member_name}</td>
                              <td>{row.mobile || '-'}</td>
                              <td>{row.outlet_name || '-'}</td>
                              <td>INR {row.amount}</td>
                              <td>{row.method || '-'}</td>
                              <td>{row.transaction_id || '-'}</td>
                              <td>{row.date_time ? new Date(row.date_time).toLocaleString() : '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
                </div>
              </div>
            </article>
          ) : null}
          {activeMenu === 'Alert Queue' ? (
            <article className="panel">
              <div className="section-header alert-center-header">
                <div>
                  <ContextTitle label="Alert Center" name={selectedOutletLabel} />
                  <p className="muted">
                    Growth reminders are sent automatically by email at the fixed due-date intervals.
                  </p>
                </div>
                <div className="row-actions">
                  <label>
                    Status
                    <select className="outlet-filter" value={alertQueueStatusTab} onChange={(e) => setAlertQueueStatusTab(e.target.value)}>
                      <option value="ALL">All</option>
                      <option value="QUEUED">Queued</option>
                      <option value="SENT">Sent</option>
                      <option value="FAILED">Failed</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="alert-center-grid">
                <section className={`alert-program-card alert-program-card--${alertPlanViewEffective}`}>
                  <div className="alert-program-card__topline">
                    <span>Growth</span>
                    <strong>Automated Email Reminders</strong>
                  </div>
                  <div className="alert-program-stats">
                    <span><strong>6</strong>Total</span>
                    <span><strong>3</strong>Before</span>
                    <span><strong>1</strong>Due</span>
                    <span><strong>2</strong>After</span>
                  </div>
                  <p className="alert-program-note">Automated reminders are sent by email on the fixed due-date schedule.</p>
                </section>
                <section className="alert-stage-card">
                  {GROWTH_ALERT_STAGES.map((stage) => (
                    <div key={stage.key} className={`alert-stage-pill alert-stage-pill--${stage.bucket}`}>
                      <span>{stage.label}</span>
                      {stage.helper ? <small>{stage.helper}</small> : null}
                    </div>
                  ))}
                </section>
                <section className="alert-status-card">
                  <span><strong>{alertCenterStats.queued}</strong>Queued</span>
                  <span><strong>{alertCenterStats.sent}</strong>Sent</span>
                  <span><strong>{alertCenterStats.failed}</strong>Failed</span>
                  <span><strong>{alertCenterStats.total}</strong>Total Shown</span>
                </section>
              </div>
              <div className="table-wrap">
                <table className="pro-table mobile-cards table-partner-alert-queue">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Stage</th>
                      <th>Scheduled</th>
                      <th>Member</th>
                      <th>Outlet</th>
                      <th>Sender</th>
                      <th>Message</th>
                      <th>Processed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alertCenterRows.length === 0 ? (
                      <tr><td colSpan="8">No reminders found for this filter.</td></tr>
                    ) : (
                      alertCenterRows.map((alert) => (
                        <tr key={alert.id}>
                          <td>{alert.delivery_status || '-'}</td>
                          <td>{ALERT_TYPE_LABELS[String(alert.alert_type || '').toLowerCase()] || alert.alert_type || '-'}</td>
                          <td>{alert.scheduled_for ? new Date(alert.scheduled_for).toLocaleString() : (alert.queued_at ? new Date(alert.queued_at).toLocaleString() : '-')}</td>
                          <td>{alert.member_name || '-'}</td>
                          <td>{alert.outlet_name || '-'}</td>
                          <td>{alert.queued_by_name || '-'}</td>
                          <td style={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>{alert.message_text || '-'}</td>
                          <td>{alert.processed_at ? new Date(alert.processed_at).toLocaleString() : '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>
          ) : null}
          {activeMenu === 'Analytics' ? <PartnerAnalyticsPanel partnerId={partnerId} outletId={selectedOutletId} outletName={selectedOutletLabel} /> : null}

          {planModalType ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>{planModalType === 'create' ? 'Add Plan' : 'Edit Plan'}</h2>
                <StepTabs steps={[1]} current={1} labels={PLAN_STEP_LABELS} />
                <form className="form-grid compact" onSubmit={planModalType === 'create' ? submitPlan : submitEditPlan}>
                  <label>Outlet<input value={selectedOutlet?.name || ''} readOnly /></label>
                  {planModalType === 'create' ? (
                    <>
                      <label>Plan Name<input required value={planForm.name} onChange={(e) => setPlanForm((p) => ({ ...p, name: e.target.value }))} /></label>
                      <label>Charge<input required type="number" value={planForm.charge} onChange={(e) => updatePlanCharge(e.target.value)} /></label>
                      <label>Duration Months<input required type="number" min="1" step="1" value={planForm.duration_months} onInvalid={setDurationMonthsValidity} onInput={clearDurationMonthsValidity} onChange={(e) => setPlanForm((p) => ({ ...p, duration_months: e.target.value }))} /></label>
                      <label>Description<input value={planForm.description} onChange={(e) => setPlanForm((p) => ({ ...p, description: e.target.value }))} /></label>
                      <label>Currency
                        <select value={planForm.currency || 'INR'} onChange={(e) => setPlanForm((p) => ({ ...p, currency: e.target.value }))}>
                          {CURRENCIES.map(currency => (
                            <option key={currency.code} value={currency.code}>
                              {currency.symbol} - {currency.code} ({currency.name})
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>Discount %<input type="number" min="0" max="100" step="0.01" value={planForm.discount_pct} onChange={(e) => updatePlanDiscountPercent(e.target.value)} /></label>
                      <label>Discount Amount<input type="number" min="0" step="0.01" value={planForm.discount_amount} onChange={(e) => updatePlanDiscountAmount(e.target.value)} /></label>
                      <label className="checkline"><input type="checkbox" checked={planForm.trainer_support} onChange={(e) => setPlanForm((p) => ({ ...p, trainer_support: e.target.checked }))} />Trainer Support</label>
                    </>
                  ) : (
                    <>
                      <label>Plan Name<input value={editingPlan?.name || ''} onChange={(e) => setEditingPlan((p) => ({ ...p, name: e.target.value }))} /></label>
                      <label>Charge<input type="number" value={editingPlan?.charge || ''} onChange={(e) => updateEditingPlanCharge(e.target.value)} /></label>
                      <label>Duration<input type="number" min="1" step="1" value={editingPlan?.duration_months || ''} onInvalid={setDurationMonthsValidity} onInput={clearDurationMonthsValidity} onChange={(e) => setEditingPlan((p) => ({ ...p, duration_months: e.target.value }))} /></label>
                      <label>Description<input value={editingPlan?.description || ''} onChange={(e) => setEditingPlan((p) => ({ ...p, description: e.target.value }))} /></label>
                      <label>Discount %<input type="number" min="0" max="100" step="0.01" value={editingPlan?.discount_pct || ''} onChange={(e) => updateEditingPlanDiscountPercent(e.target.value)} /></label>
                      <label>Discount Amount<input type="number" min="0" step="0.01" value={editingPlan?.discount_amount || ''} onChange={(e) => updateEditingPlanDiscountAmount(e.target.value)} /></label>
                    </>
                  )}
                  <div className="row-actions">
                    <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}><SaveOutlinedIcon fontSize="small" />{actionStatus === 'loading' ? 'Saving...' : 'Save'}</button>
                    <button
                      className="btn-link btn-cancel"
                      type="button"
                      onClick={() => {
                        setPlanModalType(null);
                        if (planModalType === 'edit') {
                          setEditingPlan(null);
                          setEditingPlanDiscountSource('percent');
                        }
                      }}
                    >
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </form>
              </article>
            </div>
          ) : null}

          {planActionConfirm ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>{pendingPlanActionLabel} Plan</h2>
                <div className="form-grid compact">
                  <p className="muted">
                    Are you sure you want to {pendingPlanActionLabel.toLowerCase()} "{getOutletPlanLabel(planActionConfirm.plan)}"?
                  </p>
                  <div className="row-actions">
                    <button className={`btn-link ${pendingPlanActionClass}`} type="button" onClick={confirmPlanAction} disabled={actionStatus === 'loading'}>
                      {pendingPlanActionIsDelete ? (
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      ) : planActionConfirm.plan?.active ? (
                        <BlockOutlinedIcon fontSize="small" />
                      ) : (
                        <CheckCircleOutlineOutlinedIcon fontSize="small" />
                      )}
                      {actionStatus === 'loading' ? 'Working...' : pendingPlanActionLabel}
                    </button>
                    <button className="btn-link btn-cancel" type="button" onClick={() => setPlanActionConfirm(null)} disabled={actionStatus === 'loading'}>
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {memberDeleteConfirm ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Delete Member</h2>
                <div className="form-grid compact">
                  <p className="muted">
                    Are you sure you want to delete "{memberDeleteConfirm.name || 'this member'}"? This action cannot be undone.
                  </p>
                  <div className="row-actions">
                    <button className="btn-link btn-delete" type="button" onClick={confirmMemberDelete} disabled={actionStatus === 'loading'}>
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                      {actionStatus === 'loading' ? 'Deleting...' : 'Delete'}
                    </button>
                    <button className="btn-link btn-cancel" type="button" onClick={() => setMemberDeleteConfirm(null)} disabled={actionStatus === 'loading'}>
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {showMemberOnboarding ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Member Onboarding</h2>
                <StepTabs steps={[1, 2, 3, 4, 5]} current={memberStep} labels={MEMBER_ONBOARDING_STEP_LABELS} />
                {memberStep === 1 ? (
                  <form className="form-grid compact" onSubmit={submitMemberStepOne}>
                    <label>Outlet<input value={selectedMemberOutlet?.name || selectedOutlet?.name || ''} readOnly /></label>
                    <label>Name<input required value={memberForm.name} onChange={(e) => setMemberForm((p) => ({ ...p, name: e.target.value }))} /></label>
                    <label>Email<input required type="email" value={memberForm.email} onChange={(e) => setMemberForm((p) => ({ ...p, email: e.target.value }))} /></label>
                    <label>Mobile<input required type="tel" inputMode="tel" autoComplete="tel" pattern={'\\+?[0-9 ()\\-]{8,20}'} title="Enter a valid mobile number with 8 to 15 digits." value={memberForm.mobile} onChange={(e) => setMemberForm((p) => ({ ...p, mobile: e.target.value }))} /></label>
                    <label>
                      Weight
                      <div className="inline-field-group">
                        <input required type="number" min="0" step="0.1" value={memberForm.weight} onChange={(e) => setMemberForm((p) => ({ ...p, weight: e.target.value }))} />
                        <select value={memberForm.weight_unit} onChange={(e) => setMemberForm((p) => ({ ...p, weight_unit: e.target.value }))}>
                          {WEIGHT_UNITS.map((unit) => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                        </select>
                      </div>
                    </label>
                    <label>
                      Height
                      <select value={memberForm.height_unit} onChange={(e) => setMemberForm((p) => ({ ...p, height_unit: e.target.value, height: '', height_feet: '', height_inches: '' }))}>
                        {HEIGHT_UNITS.map((unit) => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                      </select>
                    </label>
                    {memberForm.height_unit === 'ft_in' ? (
                      <div className="inline-field-group">
                        <label>Feet<input required type="number" min="0" step="1" value={memberForm.height_feet} onChange={(e) => setMemberForm((p) => ({ ...p, height_feet: e.target.value }))} /></label>
                        <label>Inches<input required type="number" min="0" max="11.99" step="0.1" value={memberForm.height_inches} onChange={(e) => setMemberForm((p) => ({ ...p, height_inches: e.target.value }))} /></label>
                      </div>
                    ) : (
                      <label>Height (cm)<input required type="number" min="0" step="0.1" value={memberForm.height} onChange={(e) => setMemberForm((p) => ({ ...p, height: e.target.value }))} /></label>
                    )}
                    <label>
                      Signup Fee Override
                      <input
                        type="number"
                        value={memberForm.signup_fee_override}
                        placeholder={`Default: ${outletSignupFeeDefault}`}
                        disabled={memberForm.signup_fee_exempt}
                        onChange={(e) => setMemberForm((p) => ({ ...p, signup_fee_override: e.target.value }))}
                      />
                    </label>
                    <label className="checkline">
                      <input
                        type="checkbox"
                        checked={memberForm.signup_fee_exempt}
                        onChange={(e) => setMemberForm((p) => ({ ...p, signup_fee_exempt: e.target.checked }))}
                      />
                      Exempt Signup Fee
                    </label>
                    <label>
                      Discount Type
                      <select
                        className="outlet-filter"
                        value={memberForm.member_discount_mode}
                        onChange={(e) => setMemberForm((p) => ({
                          ...p,
                          member_discount_mode: e.target.value,
                          member_discount_pct: e.target.value === 'none' ? '' : p.member_discount_pct
                        }))}
                      >
                        <option value="none">No Discount</option>
                        <option value="one_time">One-Time Discount</option>
                        <option value="every_cycle">Every-Cycle Discount</option>
                      </select>
                    </label>
                    <label>
                      Discount %
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={memberForm.member_discount_pct}
                        disabled={memberForm.member_discount_mode === 'none'}
                        onChange={(e) => setMemberForm((p) => ({ ...p, member_discount_pct: clampPercentInput(e.target.value) }))}
                      />
                    </label>
                    <label>
                      Profile Picture (Live)
                      <input
                        ref={profileFileInputRef}
                        type="file"
                        accept="image/*"
                        capture="user"
                        onChange={onProfilePhotoChange}
                        style={{ display: 'none' }}
                      />
                      <div className="row-actions">
                        <button className="btn-link btn-primary-action mini" type="button" onClick={openProfileCamera}>
                          <CameraAltOutlinedIcon fontSize="small" />
                          Open Camera
                        </button>
                        <button className="btn-link btn-cancel mini" type="button" onClick={() => profileFileInputRef.current?.click()}>
                          <FileUploadOutlinedIcon fontSize="small" />
                          Choose File
                        </button>
                      </div>
                      {profileCameraModal.error ? <p className="error">{profileCameraModal.error}</p> : null}
                    </label>
                    {memberProfilePreview ? (
                      <div className="full-grid-span">
                        <p className="muted">Profile Preview</p>
                        <img src={memberProfilePreview} alt="Profile preview" className="member-avatar-preview" />
                      </div>
                    ) : null}
                    <div className="row-actions">
                      <button className="btn-link btn-primary-action" type="submit"><CheckCircleOutlineOutlinedIcon fontSize="small" />Continue</button>
                      <button className="btn-link btn-cancel" type="button" onClick={closeMemberOnboarding}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : null}
                {memberStep === 2 ? (
                  <div className="form-grid compact">
                    <label>Email OTP<input value={memberOtp.email} onChange={(e) => setMemberOtp((p) => ({ ...p, email: e.target.value }))} /></label>
                    <button className="btn-link btn-save" type="button" onClick={submitMemberOtp}><CheckCircleOutlineOutlinedIcon fontSize="small" />{memberOtpVerified.email ? 'Email Verified' : 'Verify Email'}</button>
                    <button className="btn-link btn-primary-action" type="button" onClick={() => setMemberStep(3)}><CheckCircleOutlineOutlinedIcon fontSize="small" />Verify Later</button>
                    <button className="btn-link btn-cancel" type="button" onClick={closeMemberOnboarding}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                  </div>
                ) : null}
                {memberStep === 3 ? (
                  <form className="form-grid compact" onSubmit={submitMemberStepThree}>
                    <label>Document Type<input value={memberDocType} disabled={skipMemberDocumentUpload} onChange={(e) => setMemberDocType(e.target.value)} /></label>
                    <label className="checkline" style={{ gridColumn: '1 / -1' }}>
                      <input
                        type="checkbox"
                        checked={skipMemberDocumentUpload}
                        onChange={(e) => {
                          setSkipMemberDocumentUpload(e.target.checked);
                          if (e.target.checked) {
                            setMemberDocuments([]);
                          }
                        }}
                      />
                      Upload later
                    </label>
                    <label>
                      Documents
                      <input
                        key={skipMemberDocumentUpload ? 'skip-member-documents' : 'upload-member-documents'}
                        type="file"
                        multiple
                        required={!skipMemberDocumentUpload}
                        disabled={skipMemberDocumentUpload}
                        onChange={(e) => setMemberDocuments(Array.from(e.target.files || []))}
                      />
                    </label>
                    <div className="row-actions">
                      <button className="btn-link btn-primary-action" type="submit"><CheckCircleOutlineOutlinedIcon fontSize="small" />{skipMemberDocumentUpload ? 'Skip & Continue' : 'Continue'}</button>
                      <button className="btn-link btn-cancel" type="button" onClick={closeMemberOnboarding}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : null}
                {memberStep === 4 ? (
                  <form className="form-grid compact" onSubmit={submitMemberStepFour}>
                    <label>
                      Available Plans
                      <select className="outlet-filter" required value={memberSelectedPlanId} onChange={(e) => setMemberSelectedPlanId(e.target.value)}>
                        <option value="">
                          {memberAvailablePlans.length > 0 ? 'Select Plan' : 'No active plans available'}
                        </option>
                        {memberAvailablePlans.map((plan) => (
                          <option key={plan.plan_id} value={plan.plan_id}>
                            {getPlanOptionLabel(plan)}
                          </option>
                        ))}
                      </select>
                      {memberAvailablePlans.length === 0 ? (
                        <p className="muted">Create and activate a plan for this outlet before assigning it to a member.</p>
                      ) : null}
                    </label>
                    <label>
                      Plan Start Date
                      <input type="date" value={memberPlanStartDate} onChange={(e) => setMemberPlanStartDate(e.target.value)} />
                    </label>
                    <div className="row-actions">
                      <button className="btn-link btn-save" type="submit"><SaveOutlinedIcon fontSize="small" />Finish</button>
                      <button className="btn-link btn-cancel" type="button" onClick={closeMemberOnboarding}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : null}
                {memberStep === 5 ? (
                  <div className="invoice-card">
                    <div className="invoice-hero">
                      <div>
                        <p className="invoice-eyebrow">Step 5 - Payment Summary</p>
                        <h3>Member Enrollment Invoice</h3>
                        <p className="muted">{selectedMemberName}</p>
                      </div>
                      <div className="invoice-status">
                        <span className="invoice-chip">Pending</span>
                        <div className="invoice-total">
                          <span className="muted">Total Due</span>
                          <strong>{invoiceCurrency} {invoiceTotal}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="invoice-kpis">
                      <div className="invoice-kpi">
                        <span>Outlet</span>
                        <strong>{selectedMemberOutlet?.name || '-'}</strong>
                      </div>
                      <div className="invoice-kpi">
                        <span>Plan</span>
                        <strong>{selectedPlanLabel}</strong>
                      </div>
                      <div className="invoice-kpi">
                        <span>Plan Start</span>
                        <strong>{planStartLabel}</strong>
                      </div>
                      <div className="invoice-kpi">
                        <span>Payment Method</span>
                        <strong>Cash</strong>
                      </div>
                      <div className="invoice-kpi">
                        <span>Invoice Date</span>
                        <strong>{invoiceDateLabel}</strong>
                      </div>
                    </div>

                    <div className="invoice-table">
                      <div className="invoice-row">
                        <span>Plan Fee</span>
                        <strong>{invoiceCurrency} {memberPaymentQuote?.base_amount ?? 0}</strong>
                      </div>
                      <div className="invoice-row">
                        <span>Plan Discount ({memberPaymentQuote?.plan_discount_pct ?? 0}%)</span>
                        <strong>- {invoiceCurrency} {memberPaymentQuote?.plan_discount_amount ?? 0}</strong>
                      </div>
                      <div className="invoice-row">
                        <span>
                          Member Discount ({memberPaymentQuote?.member_discount_mode === 'every_cycle' ? 'Every Cycle' : memberPaymentQuote?.member_discount_mode === 'one_time' ? 'One Time' : 'None'} - {memberPaymentQuote?.member_discount_pct ?? 0}%)
                        </span>
                        <strong>- {invoiceCurrency} {memberPaymentQuote?.member_discount_amount ?? 0}</strong>
                      </div>
                      <div className="invoice-row">
                        <span>Total Discount ({memberPaymentQuote?.discount_pct ?? 0}%)</span>
                        <strong>- {invoiceCurrency} {memberPaymentQuote?.discount_amount ?? 0}</strong>
                      </div>
                      <div className="invoice-row">
                        <span>Signup Fee</span>
                        <strong>{invoiceCurrency} {memberPaymentQuote?.signup_fee ?? 0}</strong>
                      </div>
                      <div className="invoice-divider" />
                      <div className="invoice-row total">
                        <span>Total Due</span>
                        <strong>{invoiceCurrency} {invoiceTotal}</strong>
                      </div>
                    </div>

                    {memberPaymentIntent?.upi_configured ? (
                      <div className="invoice-qr">
                        <div>
                          <p className="invoice-note-title">UPI QR (optional)</p>
                          <p className="invoice-note">
                            {memberPaymentIntent.upi_id} - {invoiceCurrency} {memberPaymentIntent.amount ?? invoiceTotal}
                          </p>
                        </div>
                        <img src={memberPaymentIntent.qr_code_url} alt="UPI QR" className="qr-code-image" />
                      </div>
                    ) : (
                      <div className="invoice-qr">
                        <div>
                          <p className="invoice-note-title">UPI not configured</p>
                          <p className="invoice-note">Add the partner UPI ID in partner onboarding details to show a payment QR here.</p>
                        </div>
                      </div>
                    )}

                    <div className="row-actions invoice-actions">
                      <button className="btn-link btn-cash" type="button" onClick={() => setCashPaymentConfirmOpen(true)} disabled={actionStatus === 'loading'}>
                        <PaidOutlinedIcon fontSize="small" />
                        {actionStatus === 'loading' ? 'Confirming...' : 'Confirm Cash Received'}
                      </button>
                      <button className="btn-link btn-cancel" type="button" onClick={closeMemberOnboarding}>
                        <CloseOutlinedIcon fontSize="small" />
                        Close
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            </div>
          ) : null}

          {cashPaymentConfirmOpen ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="cash-payment-confirm-title">
              <article className="panel crud-modal">
                <h2 id="cash-payment-confirm-title">Confirm cash received</h2>
                <div>
                  <p>Confirm receipt of {invoiceCurrency} {invoiceTotal.toFixed(2)} in cash. This activates the membership and emails the member a welcome invoice.</p>
                  <div className="row-actions">
                    <button className="btn-link btn-cash" type="button" onClick={async () => { setCashPaymentConfirmOpen(false); await confirmMemberOnboardingPayment(); }} disabled={actionStatus === 'loading'}>
                      <PaidOutlinedIcon fontSize="small" />Yes, confirm payment
                    </button>
                    <button className="btn-link btn-cancel" type="button" onClick={() => setCashPaymentConfirmOpen(false)} disabled={actionStatus === 'loading'}>
                      <CloseOutlinedIcon fontSize="small" />Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {editingMember ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Edit Member</h2>
                <StepTabs
                  steps={[1, 2, 3]}
                  current={editingMemberStep}
                  onStepChange={setEditingMemberStep}
                  labels={EDIT_MEMBER_STEP_LABELS}
                />
                {editingMemberStep === 1 ? (
                  <form className="form-grid compact" onSubmit={submitEditMember}>
                    <label>Outlet<input value={editingMember.outlet_name || selectedOutlet?.name || ''} readOnly /></label>
                    <label>Name<input value={editingMember.name} onChange={(e) => setEditingMember((p) => ({ ...p, name: e.target.value }))} /></label>
                    <label>Email<input value={editingMember.email} onChange={(e) => setEditingMember((p) => ({ ...p, email: e.target.value }))} /></label>
                    <label>Mobile<input value={editingMember.mobile} onChange={(e) => setEditingMember((p) => ({ ...p, mobile: e.target.value }))} /></label>
                    <label>
                      Weight
                      <div className="inline-field-group">
                        <input type="number" min="0" step="0.1" value={editingMember.weight ?? ''} onChange={(e) => setEditingMember((p) => ({ ...p, weight: e.target.value }))} />
                        <select value={editingMember.weight_unit || 'kg'} onChange={(e) => setEditingMember((p) => ({ ...p, weight_unit: e.target.value }))}>
                          {WEIGHT_UNITS.map((unit) => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                        </select>
                      </div>
                    </label>
                    <label>
                      Height
                      <select value={editingMember.height_unit || 'cm'} onChange={(e) => setEditingMember((p) => ({ ...p, height_unit: e.target.value, height: '', height_feet: '', height_inches: '' }))}>
                        {HEIGHT_UNITS.map((unit) => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                      </select>
                    </label>
                    {(editingMember.height_unit || 'cm') === 'ft_in' ? (
                      <div className="inline-field-group">
                        <label>Feet<input type="number" min="0" step="1" value={editingMember.height_feet ?? ''} onChange={(e) => setEditingMember((p) => ({ ...p, height_feet: e.target.value }))} /></label>
                        <label>Inches<input type="number" min="0" max="11.99" step="0.1" value={editingMember.height_inches ?? ''} onChange={(e) => setEditingMember((p) => ({ ...p, height_inches: e.target.value }))} /></label>
                      </div>
                    ) : (
                      <label>Height (cm)<input type="number" min="0" step="0.1" value={editingMember.height ?? ''} onChange={(e) => setEditingMember((p) => ({ ...p, height: e.target.value }))} /></label>
                    )}
                    <label>Other Info<input value={editingMember.other_info || ''} onChange={(e) => setEditingMember((p) => ({ ...p, other_info: e.target.value }))} /></label>
                    <label>
                      Signup Fee Override
                      <input
                        type="number"
                        value={editingMember.signup_fee_override ?? ''}
                        disabled={!!editingMember.signup_fee_exempt}
                        onChange={(e) => setEditingMember((p) => ({ ...p, signup_fee_override: e.target.value }))}
                      />
                    </label>
                    <label className="checkline">
                      <input
                        type="checkbox"
                        checked={!!editingMember.signup_fee_exempt}
                        onChange={(e) => setEditingMember((p) => ({ ...p, signup_fee_exempt: e.target.checked }))}
                      />
                      Exempt Signup Fee
                    </label>
                    <label>
                      Discount Type
                      <select
                        className="outlet-filter"
                        value={editingMember.member_discount_mode || 'none'}
                        onChange={(e) => setEditingMember((p) => ({
                          ...p,
                          member_discount_mode: e.target.value,
                          member_discount_pct: e.target.value === 'none' ? '' : p.member_discount_pct
                        }))}
                      >
                        <option value="none">No Discount</option>
                        <option value="one_time">One-Time Discount</option>
                        <option value="every_cycle">Every-Cycle Discount</option>
                      </select>
                    </label>
                    <label>
                      Discount %
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={editingMember.member_discount_pct ?? ''}
                        disabled={(editingMember.member_discount_mode || 'none') === 'none'}
                        onChange={(e) => setEditingMember((p) => ({ ...p, member_discount_pct: clampPercentInput(e.target.value) }))}
                      />
                    </label>
                    <div className="row-actions">
                      <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}><SaveOutlinedIcon fontSize="small" />Save Details</button>
                      <button className="btn-link btn-cancel" type="button" onClick={() => setEditingMember(null)}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : null}
                {editingMemberStep === 2 ? (
                  <form className="form-grid compact" onSubmit={submitEditMemberDocuments}>
                    <label>Document Type<input value={editingMemberDocType} onChange={(e) => setEditingMemberDocType(e.target.value)} /></label>
                    <label>Documents<input type="file" multiple required onChange={(e) => setEditingMemberDocuments(Array.from(e.target.files || []))} /></label>
                    <div className="row-actions">
                      <button className="btn-link btn-primary-action" type="submit" disabled={actionStatus === 'loading'}><FileUploadOutlinedIcon fontSize="small" />{actionStatus === 'loading' ? 'Uploading...' : 'Continue'}</button>
                      <button className="btn-link btn-cancel" type="button" onClick={() => setEditingMember(null)}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : null}
                {editingMemberStep === 3 ? (
                  <form className="form-grid compact" onSubmit={submitEditMemberPlan}>
                    <label>
                      Available Plans
                      <select className="outlet-filter" required value={editingMemberSelectedPlanId} onChange={(e) => setEditingMemberSelectedPlanId(e.target.value)}>
                        <option value="">
                          {editingMemberAvailablePlans.length > 0 ? 'Select Plan' : 'No active plans available'}
                        </option>
                        {editingMemberAvailablePlans.map((plan) => (
                          <option key={plan.plan_id} value={plan.plan_id}>
                            {getPlanOptionLabel(plan)}
                          </option>
                        ))}
                      </select>
                      {editingMemberAvailablePlans.length === 0 ? (
                        <p className="muted">Create and activate a plan for this outlet before assigning it to this member.</p>
                      ) : null}
                    </label>
                    <label>
                      Plan Start Date
                      <input type="date" value={editingMemberPlanStartDate} onChange={(e) => setEditingMemberPlanStartDate(e.target.value)} />
                    </label>
                    <div className="row-actions">
                      <button className="btn-link btn-save" type="submit" disabled={actionStatus === 'loading'}><SaveOutlinedIcon fontSize="small" />{actionStatus === 'loading' ? 'Saving...' : 'Finish'}</button>
                      <button className="btn-link btn-cancel" type="button" onClick={() => setEditingMember(null)}><CloseOutlinedIcon fontSize="small" />Cancel</button>
                    </div>
                  </form>
                ) : null}
              </article>
            </div>
          ) : null}

          {profileCameraModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Capture Profile Photo</h2>
                <div className="form-grid">
                  <video ref={profileVideoRef} className="camera-preview" autoPlay playsInline muted />
                  <canvas ref={profileCanvasRef} style={{ display: 'none' }} />
                  <div className="row-actions">
                    <button className="btn-link btn-save" type="button" onClick={captureProfileFromCamera}>
                      <CameraAltOutlinedIcon fontSize="small" />
                      Capture
                    </button>
                    <button className="btn-link btn-cancel" type="button" onClick={closeProfileCameraModal}>
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {false && manualAlertModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Queue Manual Reminder</h2>
                <div className="form-grid compact">
                  <label>Member<input value={manualAlertModal?.row?.member_name || ''} readOnly /></label>
                  <label>Outlet<input value={manualAlertModal?.row?.outlet_name || ''} readOnly /></label>
                  <label>
                    Reminder Slot
                    <select value={manualAlertModal.alertType} onChange={(e) => updateManualAlertStage(e.target.value)}>
                      {GROWTH_ALERT_STAGES.map((stage) => (
                        <option key={stage.key} value={stage.key}>{stage.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Scheduled Date
                    <input type="date" value={manualAlertModal.scheduledDate} onChange={(e) => setManualAlertModal((prev) => ({ ...prev, scheduledDate: e.target.value }))} />
                  </label>
                  <p className="modal-inline-note">
                    {GROWTH_ALERT_STAGES.find((stage) => stage.key === manualAlertModal.alertType)?.helper || 'Queue this reminder for the selected schedule.'}
                  </p>
                  <label>Message<textarea className="text-area" value={manualAlertModal.message} onChange={(e) => setManualAlertModal((prev) => ({ ...prev, message: e.target.value }))} /></label>
                  <div className="row-actions">
                    <button className="btn-link btn-alert" type="button" onClick={sendManualAlertFromModal} disabled={actionStatus === 'loading'}>
                      <NotificationsActiveOutlinedIcon fontSize="small" />
                      {actionStatus === 'loading' ? 'Queueing...' : 'Queue Reminder'}
                    </button>
                    <button className="btn-link btn-cancel" type="button" onClick={() => setManualAlertModal({ open: false, row: null, mode: 'email', alertType: 'manual_before_due_1', scheduledDate: '', message: '' })}>
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {markPaidModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Mark as Paid</h2>
                <div className="form-grid compact">
                  <label>Member<input value={markPaidModal?.row?.member_name || ''} readOnly /></label>
                  <label>Amount (INR)<input value={markPaidModal?.row?.due_amount ?? markPaidModal?.row?.amount ?? ''} readOnly /></label>
                  <label>Transaction ID (Optional)<input value={markPaidModal.transactionId} onChange={(e) => setMarkPaidModal((prev) => ({ ...prev, transactionId: e.target.value }))} /></label>
                  <div className="row-actions">
                    <button className="btn-link btn-paid" type="button" onClick={confirmMarkPaid} disabled={actionStatus === 'loading'}>
                      <PaidOutlinedIcon fontSize="small" />
                      {actionStatus === 'loading' ? 'Saving...' : 'Save'}
                    </button>
                    <button className="btn-link btn-cancel" type="button" onClick={() => setMarkPaidModal({ open: false, row: null, transactionId: '' })}>
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {holdModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Put Member On Hold</h2>
                <div className="form-grid compact">
                  <label>Member<input value={holdModal?.row?.member_name || ''} readOnly /></label>
                  <label>Outlet<input value={holdModal?.row?.outlet_name || ''} readOnly /></label>
                  <label>
                    Hold Months (Max 3)
                    <select
                      className="outlet-filter"
                      value={holdModal.holdMonths}
                      onChange={(e) => setHoldModal((prev) => ({ ...prev, holdMonths: Number(e.target.value) }))}
                    >
                      <option value={1}>1 Month</option>
                      <option value={2}>2 Months</option>
                      <option value={3}>3 Months</option>
                    </select>
                  </label>
                  <label>
                    Holding Charge / Month (INR)
                    <input
                      type="number"
                      min="0"
                      value={holdModal.holdingChargePerMonth}
                      onChange={(e) => setHoldModal((prev) => ({ ...prev, holdingChargePerMonth: e.target.value }))}
                      placeholder="Enter amount"
                    />
                  </label>
                  <label>
                    Total Hold Charge (INR)
                    <input
                      value={Number(holdModal.holdMonths || 0) * Number(holdModal.holdingChargePerMonth || 0)}
                      readOnly
                    />
                  </label>
                  <div className="row-actions">
                    <button className="btn-link btn-disable" type="button" onClick={submitHold} disabled={actionStatus === 'loading'}>
                      <BlockOutlinedIcon fontSize="small" />
                      {actionStatus === 'loading' ? 'Saving...' : 'Confirm Hold'}
                    </button>
                    <button
                      className="btn-link btn-cancel"
                      type="button"
                      onClick={() => setHoldModal({ open: false, row: null, holdMonths: 1, holdingChargePerMonth: '' })}
                    >
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {resumeHoldModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Pay Holding Charge & Resume</h2>
                <div className="form-grid compact">
                  <label>Member<input value={resumeHoldModal?.row?.member_name || ''} readOnly /></label>
                  <label>Outlet<input value={resumeHoldModal?.row?.outlet_name || ''} readOnly /></label>
                  <label>Required Hold Charge (INR)<input value={resumeHoldModal?.row?.hold_fee_paid ?? 0} readOnly /></label>
                  <label>
                    Amount Paid (INR)
                    <input
                      type="number"
                      min="0"
                      value={resumeHoldModal.amount}
                      onChange={(e) => setResumeHoldModal((prev) => ({ ...prev, amount: e.target.value }))}
                    />
                  </label>
                  <label>
                    Payment Method
                    <select
                      className="outlet-filter"
                      value={resumeHoldModal.method}
                      onChange={(e) => setResumeHoldModal((prev) => ({ ...prev, method: e.target.value }))}
                    >
                      <option value="UPI">UPI</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </select>
                  </label>
                  <label>
                    Transaction ID (Optional)
                    <input
                      value={resumeHoldModal.transactionId}
                      onChange={(e) => setResumeHoldModal((prev) => ({ ...prev, transactionId: e.target.value }))}
                      placeholder="Txn reference"
                    />
                  </label>
                  <div className="row-actions">
                    <button className="btn-link btn-paid" type="button" onClick={submitResumeHold} disabled={actionStatus === 'loading'}>
                      <PaidOutlinedIcon fontSize="small" />
                      {actionStatus === 'loading' ? 'Processing...' : 'Mark Paid & Resume'}
                    </button>
                    <button
                      className="btn-link btn-cancel"
                      type="button"
                      onClick={() => setResumeHoldModal({ open: false, row: null, amount: '', method: 'UPI', transactionId: '' })}
                    >
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {upiModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>UPI Payment</h2>
                <div className="form-grid compact">
                  <label>UPI ID<input value={billingSummary?.partner_upi_id || ''} readOnly /></label>
                  <label>Amount (INR)<input value={upiModal?.row?.due_amount ?? upiModal?.row?.amount ?? ''} readOnly /></label>
                  {upiModal.qrCodeUrl ? (
                    <div className="full-grid-span">
                      <p className="muted">Scan in GPay or Paytm</p>
                      <img src={upiModal.qrCodeUrl} alt="UPI QR" className="qr-code-image" />
                    </div>
                  ) : null}
                  {upiModal.upiUri ? (
                    <a className="btn-link btn-upi mini" href={upiModal.upiUri}><QrCode2OutlinedIcon fontSize="small" />Open UPI App</a>
                  ) : null}
                  <div className="row-actions">
                    <button className="btn-link btn-paid" type="button" onClick={() => {
                      setMarkPaidModal({ open: true, row: upiModal.row, transactionId: '' });
                      setUpiModal({ open: false, row: null, upiUri: '', qrCodeUrl: '' });
                    }}><PaidOutlinedIcon fontSize="small" />Mark Paid</button>
                    <button className="btn-link btn-cancel" type="button" onClick={() => setUpiModal({ open: false, row: null, upiUri: '', qrCodeUrl: '' })}><CloseOutlinedIcon fontSize="small" />Close</button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {dueHistoryModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Due History (Last 3 Months)</h2>
                <p className="muted">{dueHistoryModal?.row?.member_name || ''}</p>
                <div className="table-wrap">
                  <table className="pro-table mobile-cards table-due-history">
                    <thead>
                      <tr><th>Date</th><th>Amount</th><th>Method</th><th>Transaction</th></tr>
                    </thead>
                    <tbody>
                      {dueHistoryModal.history.length === 0 ? (
                        <tr><td colSpan="4">No payment records in last 3 months.</td></tr>
                      ) : (
                        dueHistoryModal.history.map((h) => (
                          <tr key={h.id}>
                            <td>{h.date_time ? new Date(h.date_time).toLocaleString() : '-'}</td>
                            <td>INR {h.amount ?? 0}</td>
                            <td>{h.method || '-'}</td>
                            <td>{h.transaction_id || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="row-actions">
                  <button className="btn-link btn-cancel" type="button" onClick={() => setDueHistoryModal({ open: false, row: null, history: [] })}><CloseOutlinedIcon fontSize="small" />Close</button>
                </div>
              </article>
            </div>
          ) : null}

          {discontinueEmailModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Send Discontinuation Email</h2>
                <div className="form-grid compact">
                  <label>Member<input value={discontinueEmailModal.row?.member_name || ''} readOnly /></label>
                  <label>Outlet<input value={discontinueEmailModal.row?.outlet_name || ''} readOnly /></label>
                  <label>Subject<input value={buildDiscontinueEmailSubject(discontinueEmailModal.row)} readOnly /></label>
                  <label>Message<textarea rows={6} value={buildDiscontinueEmailBody(discontinueEmailModal.row)} readOnly /></label>
                  {discontinueEmailModal.error ? <p className="error">{discontinueEmailModal.error}</p> : null}
                  <div className="row-actions">
                    <button className="btn-link btn-alert" type="button" onClick={confirmDiscontinueEmail} disabled={discontinueEmailModal.sending}>
                      <NotificationsActiveOutlinedIcon fontSize="small" />
                      {discontinueEmailModal.sending ? 'Sending...' : 'Send Email & Disable Member'}
                    </button>
                    <button className="btn-link btn-cancel" type="button" onClick={closeDiscontinueEmailModal} disabled={discontinueEmailModal.sending}>
                      <CloseOutlinedIcon fontSize="small" />
                      Cancel
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          {breakupModal.open ? (
            <PriceBreakupModal
              row={breakupModal.row}
              onClose={() => setBreakupModal({ open: false, row: null })}
            />
          ) : null}

          {planLimitModal.open ? (
            <div className="crud-modal-backdrop" role="dialog" aria-modal="true">
              <article className="panel crud-modal">
                <h2>Plan Limits</h2>
                <div className="form-grid compact">
                  <label>Plan<input value={planLimitModal?.row?.plan_name || '-'} readOnly /></label>
                  <label>Billing Tier<input value={planLimitTierLabel} readOnly /></label>
                  {planLimitAlertAccess.hasManualAlerts ? (
                    <label>Manual Alerts Used / Allowed<input value={`${planLimitModal?.row?.member_alert_usage?.manual_alert ?? 0} / ${planLimitModal?.row?.member_alert_allowed?.manual_alert ?? 0}`} readOnly /></label>
                  ) : null}
                  {planLimitAlertAccess.hasAutomatedAlerts ? (
                    <label>Automated Alerts Used / Allowed<input value={`${planLimitModal?.row?.member_alert_usage?.automated_alert ?? 0} / ${planLimitModal?.row?.member_alert_allowed?.automated_alert ?? 0}`} readOnly /></label>
                  ) : null}
                  {!planLimitAlertAccess.hasAnyAlerts ? (
                    <p className="muted">Starter does not include payment reminder alerts.</p>
                  ) : null}
                  {planLimitAlertAccess.hasAnyAlerts ? (
                    <>
                      <label>Reminder Used / Allowed<input value={`${planLimitModal?.row?.member_alert_usage?.email ?? 0} / ${planLimitModal?.row?.member_alert_allowed?.email ?? 0}`} readOnly /></label>
                    </>
                  ) : null}
                </div>
                <div className="row-actions">
                  <button className="btn-link btn-cancel" type="button" onClick={() => setPlanLimitModal({ open: false, row: null })}><CloseOutlinedIcon fontSize="small" />Close</button>
                </div>
              </article>
            </div>
          ) : null}

        </section>
      </section>
    </main>
  );
}

export default PartnerDashboard;
